import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Menu,
  MenuItem,
  Popover,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Badge,
  Tooltip,
  LinearProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Receipt as ReceiptIcon,
  Description as ReportsIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  CalendarToday as CalendarIcon,
  Hotel as BedIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  MedicalServices as MedicalIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  FileDownload as DownloadIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  CurrencyRupee as MoneyIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  DirectionsWalk as OutPatientIcon,
  Emergency as EmergencyIcon,
  Sync as SyncIcon,
  SwapHoriz as ChangeRoomIcon
} from '@mui/icons-material';

// Import Firebase services
import { PatientService, DashboardService, auth } from '../services/firebase';
import RoomManagementService from '../services/RoomManagementService';
import RazorpayService from '../services/RazorpayService';
import { collection, addDoc, Timestamp, getDocs, updateDoc, doc, deleteDoc, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { format, isWithinInterval, parseISO, startOfDay, endOfDay } from 'date-fns';
import { RazorpayResponse, RazorpayError } from '../types/razorpay';

// Payment type options (matching mobile dashboard)
const PAYMENT_TYPES = [
  'Registration Fee',
  'Consultation Fee',
  'Treatment Fee',
  'Room Charges',
  'Medicine Charges',
  'Test Charges',
  'Surgery Fee',
  'Emergency Fee',
  'Other'
];

// Payment method options (matching mobile dashboard)
const PAYMENT_METHODS = ['Cash', 'Razorpay'];

// Types (converted from React Native structure)
interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  patientType: 'IP' | 'OP';
  department?: string;
  doctor: string;
  bloodGroup?: string;
  room?: string;
  bedNo?: string;
  admissionDate?: string;
  registrationDate?: string;
  statusColor: string;
  statusText: string;
  address?: string;
  email?: string;
  emergencyContact?: string;
  medicalHistory?: string[];
  allergies?: string[];
  currentMedications?: string[];
  totalAmount?: number;
  paidAmount?: number;
  dueAmount?: number;
  billChangeHistory?: Array<{
    changedBy: string;
    changedByUid: string;
    changedAt: string;
    field: string;
    oldValue: number;
    newValue: number;
    reason?: string;
  }>;
}

interface PatientPayment {
  id: string;
  patientId: string;
  patientName: string;
  date: string;
  time: string;
  amount: number;
  description: string;
  type: 'consultation' | 'tests' | 'admission' | 'surgery' | 'medicine';
  status: 'paid' | 'pending' | 'failed' | 'partial' | 'refunded';
  paymentMethod: 'cash' | 'card' | 'online' | 'upi';
  transactionId?: string;
  items?: Array<{
    name: string;
    amount: number;
  }>;
  totalAmount?: number;
  paidAmount?: number;
  dueAmount?: number;
  paymentHistory?: Array<{
    id: string;
    amount: number;
    paidDate: string;
    paymentMethod: string;
  }>;
  source?: 'payments' | 'invoices'; // Track data source for display
}

interface MedicalReport {
  id: string;
  patientId: string;
  patientName: string;
  reportType: string;
  date: string;
  doctor: string;
  department: string;
  status: 'completed' | 'pending' | 'in-progress';
  findings?: string;
  recommendations?: string;
  attachments?: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Firebase Room interface for room assignment
interface FirebaseRoom {
  id: string;
  roomNumber: string;
  roomType: string;
  capacity: number;
  currentOccupancy: number;
  availableBeds: Array<{
    bedNumber: string;
    isOccupied: boolean;
    patientId?: string;
  }>;
  department?: string;
  status: 'available' | 'occupied' | 'maintenance' | 'cleaning';
}

// Invoice generation function (matching mobile dashboard exactly)
const generateInvoiceForPatient = async (patientData: any, initialPayment: number, db: any) => {
  try {
    console.log('📄 Generating invoice for patient registration:', patientData.name);
    
    const invoiceNumber = `INV-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const currentDate = new Date();
    const dueDate = new Date();
    dueDate.setDate(currentDate.getDate() + 30); // 30 days from now
    
    const invoice = {
      invoiceNumber,
      invoiceType: 'payment_receipt',
      patientId: patientData.id,
      patientName: patientData.name,
      description: `Patient Registration Invoice - ${patientData.patientType} ${patientData.department}`,
      issueDate: currentDate.toISOString().split('T')[0],
      dueDate: dueDate.toISOString().split('T')[0],
      status: initialPayment > 0 ? 'paid' : 'draft',
      totalAmount: initialPayment > 0 ? initialPayment : patientData.paymentDetails.totalAmount,
      items: [
        {
          name: `${patientData.patientType} Registration`,
          description: `${patientData.patientType} patient registration - ${patientData.department}${initialPayment > 0 ? ` (Payment: ₹${initialPayment})` : ''}`,
          quantity: 1,
          rate: initialPayment > 0 ? initialPayment : patientData.paymentDetails.totalAmount,
          amount: initialPayment > 0 ? initialPayment : patientData.paymentDetails.totalAmount
        }
      ],
      notes: `Invoice auto-generated for patient registration on ${currentDate.toLocaleDateString('en-IN')}${initialPayment > 0 ? `. Payment of ₹${initialPayment} received.` : '. Awaiting payment.'}`,
      terms: 'Thank you for your payment',
      createdAt: currentDate.toISOString(),
      updatedAt: currentDate.toISOString(),
      paymentMethod: patientData.paymentDetails.payments[0]?.method || 'Cash',
      serviceType: patientData.patientType, // OP or IP
      isAutoGenerated: true,
      registrationDate: patientData.registrationDate,
      // Payment tracking information
      paymentDetails: {
        fullAmount: patientData.paymentDetails.totalAmount,
        actualAmountPaid: initialPayment,
        remainingAmount: Math.max(0, patientData.paymentDetails.totalAmount - initialPayment),
        paymentDate: currentDate,
        paymentMethod: patientData.paymentDetails.payments[0]?.method || 'Cash',
        transactionId: patientData.paymentDetails.payments[0]?.transactionId || null
      }
    };

    // Save to Firebase invoices collection
    const docRef = await addDoc(collection(db, 'invoices'), invoice);
    console.log('📄 Patient registration invoice generated successfully:', invoiceNumber);
    return { success: true, id: docRef.id, invoice };
  } catch (error) {
    console.error('❌ Error generating patient registration invoice:', error);
    throw error;
  }
};

// Generate invoice for a specific payment (NEW FUNCTION)
const generateInvoiceForPayment = async (paymentId: string, paymentData: any, db: any) => {
  try {
    console.log('📄 Generating invoice for payment:', paymentId);
    
    const invoiceNumber = `INV-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const currentDate = new Date();
    
    const invoice = {
      invoiceNumber,
      invoiceType: 'payment_receipt',
      
      // Patient details
      patientId: paymentData.patientId,
      patientName: paymentData.patientName,
      
      // Link to payment (1:1 relationship)
      paymentId: paymentId,
      
      // Invoice details
      issueDate: currentDate.toISOString().split('T')[0],
      issueTime: currentDate.toLocaleTimeString(),
      
      // Amount = THIS payment only (NOT cumulative)
      amount: paymentData.amount,
      totalAmount: paymentData.amount,
      paymentMethod: paymentData.paymentMethod,
      transactionId: paymentData.transactionId || `CASH-${Date.now()}`, // Generate ID for cash payments
      
      // Line items from payment
      items: paymentData.items.map((item: any) => ({
        name: item.name,
        description: item.description || '',
        quantity: item.quantity || 1,
        rate: item.rate || item.amount,
        amount: item.amount
      })),
      
      // Summary
      subtotal: paymentData.amount,
      tax: 0,
      discount: 0,
      
      // Additional info
      description: paymentData.description || `Payment Receipt - ${paymentData.purpose || 'General'}`,
      notes: `Payment receipt generated on ${currentDate.toLocaleDateString('en-IN')} at ${currentDate.toLocaleTimeString()}`,
      terms: 'Thank you for your payment',
      
      // Status
      status: 'paid',
      
      // Service type
      serviceType: paymentData.purpose || 'consultation',
      
      // Razorpay details (if applicable)
      razorpayPaymentId: paymentData.razorpayPaymentId || null,
      razorpayOrderId: paymentData.razorpayOrderId || null,
      razorpaySignature: paymentData.razorpaySignature || null,
      
      // Metadata
      isAutoGenerated: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Save invoice to Firebase
    const invoiceDoc = await addDoc(collection(db, 'invoices'), invoice);
    
    console.log('✅ Invoice generated:', invoiceNumber, 'for payment:', paymentId);
    
    return { success: true, invoiceId: invoiceDoc.id, invoice };
  } catch (error) {
    console.error('❌ Error generating invoice for payment:', error);
    throw error;
  }
};

// Transform Firebase patient data to display format
const transformPatientData = (firebasePatient: any): Patient => {
  return {
    id: firebasePatient.id,
    name: firebasePatient.name || firebasePatient.fullName || 'Unknown',
    age: firebasePatient.age || 0,
    gender: firebasePatient.gender || 'Not specified',
    phone: firebasePatient.phone || firebasePatient.phoneNumber || 'Not provided',
    patientType: firebasePatient.patientType || 'OP',
    department: firebasePatient.department || 'General',
    doctor: firebasePatient.doctor || 'Not assigned',
    bloodGroup: firebasePatient.bloodGroup,
    room: firebasePatient.room || firebasePatient.roomNumber,
    bedNo: firebasePatient.bedNo || firebasePatient.bedNumber,
    admissionDate: firebasePatient.admissionDate,
    registrationDate: firebasePatient.registrationDate || firebasePatient.createdAt,
    statusColor: firebasePatient.patientType === 'IP' ? '#3B82F6' : '#22C55E',
    statusText: firebasePatient.patientType === 'IP' ? 'Admitted' : 'Consultation',
    address: firebasePatient.address,
    email: firebasePatient.email,
    emergencyContact: firebasePatient.emergencyContact,
    medicalHistory: Array.isArray(firebasePatient.medicalHistory) 
      ? firebasePatient.medicalHistory 
      : (firebasePatient.medicalHistory ? [firebasePatient.medicalHistory] : []),
    allergies: Array.isArray(firebasePatient.allergies) 
      ? firebasePatient.allergies 
      : (firebasePatient.allergies ? [firebasePatient.allergies] : []),
    currentMedications: Array.isArray(firebasePatient.currentMedications) 
      ? firebasePatient.currentMedications 
      : (firebasePatient.currentMedications ? [firebasePatient.currentMedications] : []),
    totalAmount: firebasePatient.totalAmount || 0,
    paidAmount: firebasePatient.paidAmount || 0,
    dueAmount: firebasePatient.dueAmount || 0,
    billChangeHistory: firebasePatient.billChangeHistory || []
  };
};

// Fallback mock data (enhanced with more patients)
const fallbackPatients: Patient[] = [
  {
    id: 'KBR-OP-2025-9378237128',
    name: 'Shanker',
    age: 37,
    gender: 'Male',
    phone: '9703937700',
    patientType: 'OP',
    doctor: 'Dr Ramesh',
    department: 'General Medicine',
    bloodGroup: 'A+',
    registrationDate: '2025-10-29',
    statusColor: '#22C55E',
    statusText: 'Consultation',
    address: '123 Main Street, Hyderabad',
    email: 'shanker@email.com',
    emergencyContact: '9876543210'
  },
  {
    id: 'KBR-IP-2025-1234567890',
    name: 'Priya Sharma',
    age: 45,
    gender: 'Female',
    phone: '9876543210',
    patientType: 'IP',
    doctor: 'Dr. Sarah Johnson',
    department: 'Cardiology',
    bloodGroup: 'B+',
    room: '201',
    bedNo: '1',
    admissionDate: '2025-11-01',
    registrationDate: '2025-11-01',
    statusColor: '#3B82F6',
    statusText: 'Admitted',
    address: '456 Park Avenue, Secunderabad',
    email: 'priya.sharma@email.com',
    emergencyContact: '9123456789',
    medicalHistory: ['Hypertension', 'Diabetes Type 2'],
    allergies: ['Penicillin', 'Shellfish'],
    currentMedications: ['Metformin 500mg', 'Lisinopril 10mg', 'Aspirin 81mg']
  },
  {
    id: 'KBR-OP-2025-5555666677',
    name: 'Rajesh Kumar',
    age: 52,
    gender: 'Male',
    phone: '9555666677',
    patientType: 'OP',
    doctor: 'Dr. Michael Brown',
    department: 'Orthopedics',
    bloodGroup: 'O+',
    registrationDate: '2025-11-02',
    statusColor: '#F59E0B',
    statusText: 'Follow-up',
    address: '789 Garden Road, Banjara Hills',
    email: 'rajesh.kumar@email.com',
    emergencyContact: '9888777666'
  },
  {
    id: 'KBR-IP-2025-7777888899',
    name: 'Anita Reddy',
    age: 28,
    gender: 'Female',
    phone: '9777888899',
    patientType: 'IP',
    doctor: 'Dr. Lisa Wang',
    department: 'Obstetrics',
    bloodGroup: 'AB+',
    room: '305',
    bedNo: '2',
    admissionDate: '2025-11-03',
    registrationDate: '2025-11-03',
    statusColor: '#8B5CF6',
    statusText: 'Maternity',
    address: '321 Lake View, Jubilee Hills',
    email: 'anita.reddy@email.com',
    emergencyContact: '9444333222',
    medicalHistory: ['Previous C-Section'],
    allergies: ['Latex'],
    currentMedications: ['Prenatal Vitamins', 'Iron Supplements']
  },
  {
    id: 'KBR-OP-2025-8888999900',
    name: 'Vikram Singh',
    age: 34,
    gender: 'Male',
    phone: '9888999900',
    patientType: 'OP',
    doctor: 'Dr. Shital M',
    department: 'General Medicine',
    bloodGroup: 'A-',
    registrationDate: '2025-11-25',
    statusColor: '#22C55E',
    statusText: 'Consultation',
    address: '456 Tech City, Gachibowli',
    email: 'vikram.singh@email.com',
    emergencyContact: '9111222333'
  },
  {
    id: 'KBR-IP-2025-9999000011',
    name: 'Sunita Rao',
    age: 42,
    gender: 'Female',
    phone: '9999000011',
    patientType: 'IP',
    doctor: 'Dr. Kani Basha',
    department: 'Cardiology',
    bloodGroup: 'B-',
    room: '102',
    bedNo: '1',
    admissionDate: '2025-11-24',
    registrationDate: '2025-11-24',
    statusColor: '#3B82F6',
    statusText: 'Admitted',
    address: '789 Financial District, Nanakramguda',
    email: 'sunita.rao@email.com',
    emergencyContact: '9222333444',
    medicalHistory: ['High Cholesterol'],
    allergies: ['Aspirin'],
    currentMedications: ['Atorvastatin 20mg', 'Metoprolol 50mg']
  },
  {
    id: 'KBR-OP-2025-1111222233',
    name: 'Ramesh Babu',
    age: 58,
    gender: 'Male',
    phone: '9111222233',
    patientType: 'OP',
    doctor: 'Dr. Test Technician',
    department: 'Laboratory',
    bloodGroup: 'O-',
    registrationDate: '2025-11-23',
    statusColor: '#F59E0B',
    statusText: 'Tests Pending',
    address: '234 Old City, Charminar',
    email: 'ramesh.babu@email.com',
    emergencyContact: '9333444555'
  },
  {
    id: 'KBR-OP-2025-2222333344',
    name: 'Kavitha Devi',
    age: 29,
    gender: 'Female',
    phone: '9222333344',
    patientType: 'OP',
    doctor: 'Dr. Ramesh Kumar',
    department: 'Gynecology',
    bloodGroup: 'AB-',
    registrationDate: '2025-11-22',
    statusColor: '#8B5CF6',
    statusText: 'Consultation',
    address: '567 Begumpet, Hyderabad',
    email: 'kavitha.devi@email.com',
    emergencyContact: '9444555666'
  }
];

// ✅ GLOBAL SYNC FUNCTION: Update all payment dues to 0 when patient due = 0
const syncPaymentDuesForAllPatients = async (patients: Patient[]) => {
  try {
    console.log('🔄 Syncing payment dues for all patients...');
    console.log(`📊 Total patients to check: ${patients.length}`);
    
    // Find patients with due = 0
    const fullyPaidPatients = patients.filter(p => p.dueAmount === 0 && (p.totalAmount || 0) > 0);
    
    if (fullyPaidPatients.length === 0) {
      console.log('✓ No patients with due = 0 need syncing');
      return;
    }
    
    console.log(`📋 Found ${fullyPaidPatients.length} fully paid patients to sync:`, 
      fullyPaidPatients.map(p => `${p.name} (${p.id})`));
    
    // Update payment records for each fully paid patient
    const syncPromises = fullyPaidPatients.map(async (patient) => {
      try {
        console.log(`\n🔍 Checking patient: ${patient.name}`);
        console.log(`   Total: ₹${patient.totalAmount} | Paid: ₹${patient.paidAmount} | Due: ₹${patient.dueAmount}`);
        
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('patientId', '==', patient.id)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        
        if (paymentsSnapshot.empty) {
          console.log(`   ⚠️ No payment records found`);
          return;
        }
        
        console.log(`   📝 Found ${paymentsSnapshot.docs.length} payment records`);
        
        // Update all payment records to have dueAmount = 0
        const updatePromises = paymentsSnapshot.docs.map(async (payDoc) => {
          const payData = payDoc.data();
          
          console.log(`   💳 Payment ${payDoc.id}:`, {
            amount: payData.amount,
            oldDue: payData.dueAmount,
            newDue: 0
          });
          
          // ✅ Set ALL fields for consistency
          await updateDoc(doc(db, 'payments', payDoc.id), {
            dueAmount: 0,
            remainingAmount: 0,
            totalAmount: patient.totalAmount || payData.totalAmount || 0,
            paidAmount: patient.paidAmount || payData.paidAmount || 0,
            status: 'paid',
            paymentStatus: 'Fully Paid',
            statusColor: '#22C55E',
            updatedAt: Timestamp.now()
          });
        });
        
        await Promise.all(updatePromises);
        console.log(`   ✅ Synced ${paymentsSnapshot.docs.length} payments for ${patient.name}`);
      } catch (error) {
        console.error(`   ❌ Error syncing payments for ${patient.name}:`, error);
      }
    });
    
    await Promise.all(syncPromises);
    console.log('\n✅ Global payment dues sync completed for all patients!\n');
  } catch (error) {
    console.error('❌ Error in global payment sync:', error);
  }
};

const PatientsEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if coming from appointment with pre-filled data
  const appointmentData = location.state?.appointmentData;
  const fromAppointment = location.state?.fromAppointment;
  
  // State management (with real Firebase data)
  const [patients, setPatients] = useState<Patient[]>([]); // Start empty
  const [availableRooms, setAvailableRooms] = useState<FirebaseRoom[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(true); // Show loading initially
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [availableDoctors, setAvailableDoctors] = useState<any[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [unsubscribePatients, setUnsubscribePatients] = useState<(() => void) | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  
  // Stable callback for search to prevent re-renders
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);
  
  // Maintain focus when typing
  useEffect(() => {
    if (isSearchFocused && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchQuery, isSearchFocused]);
  
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [showChangeRoomModal, setShowChangeRoomModal] = useState(false);
  const [patientForRoomChange, setPatientForRoomChange] = useState<Patient | null>(null);
  const [newRoomNumber, setNewRoomNumber] = useState('');
  const [newBedNumber, setNewBedNumber] = useState('');
  
  // Payment processing state
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [razorpayOrderId, setRazorpayOrderId] = useState('');
  const [registrationCompleting, setRegistrationCompleting] = useState(false);
  
  // Patient registration form state
  const [registrationForm, setRegistrationForm] = useState({
    patientType: 'OP',
    fullName: '',
    age: '',
    gender: '',
    bloodGroup: '',
    phoneNumber: '',
    emergencyContact: '',
    address: '',
    doctor: '',
    department: '',
    roomNumber: '',
    bedNumber: '',
    referredBy: '',
    totalAmount: '',
    initialPayment: '',
    paymentMethod: 'Cash',
    paymentType: 'Registration Fee',
    symptoms: '',
    allergies: '',
    transactionId: '',
    razorpayPaymentId: '',
    razorpayOrderId: '',
    razorpaySignature: '',
    // Billing fields for edit
    editTotalAmount: '',
    editPaidAmount: '',
    editDueAmount: '',
    billChangeReason: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' | 'warning' | 'info' 
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  
  // Tab management state
  const [currentTab, setCurrentTab] = useState(0);
  
  // Pre-populate form if coming from appointment
  useEffect(() => {
    if (fromAppointment && appointmentData) {
      console.log('📋 Pre-populating form with appointment data:', appointmentData);
      setRegistrationForm(prev => ({
        ...prev,
        patientType: 'IP', // Default to In-Patient for admission
        fullName: appointmentData.patientName || '',
        age: appointmentData.age || '',
        gender: appointmentData.gender || 'Male',
        phoneNumber: appointmentData.phone || '',
        emergencyContact: appointmentData.emergencyContact || '',
        address: appointmentData.address || '',
        doctor: appointmentData.doctor || '',
        department: appointmentData.department || '',
        symptoms: appointmentData.symptoms || '',
        totalAmount: appointmentData.feeAmount || '',
        paymentMethod: appointmentData.paymentMode || 'Cash',
        paymentType: 'Registration Fee'
      }));
      
      // Auto-open the registration modal
      setShowRegistrationModal(true);
      
      // Clear the location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [fromAppointment, appointmentData]);
  
  // Load Firebase patients data with real-time updates
  useEffect(() => {
    const setupPatientsData = async () => {
      try {
        console.log('🔄 Setting up real-time patients data from Firebase...');
        
        // Check authentication first
        if (!auth.currentUser) {
          console.error('❌ No authenticated user found for patients data');
          setSnackbar({ 
            open: true, 
            message: 'Authentication required. Please login to continue.', 
            severity: 'error' 
          });
          setLoadingPatients(false);
          return;
        }
        
        console.log('✅ Authenticated user confirmed:', auth.currentUser.email);
        setLoadingPatients(true);
        
        // Set up real-time Firebase listener
        const unsubscribeFn = PatientService.onPatientsSnapshot((firebasePatients) => {
          console.log('👥 Received patients update from Firebase:', firebasePatients.length);
          
          const transformedPatients = firebasePatients.map(transformPatientData);
          setPatients(transformedPatients);
          setLoadingPatients(false);
          console.log('✅ Patients data updated from Firebase');
          
          // ✅ AUTO-SYNC: Check and update payment dues for patients with due = 0
          syncPaymentDuesForAllPatients(transformedPatients);
        });
        
        setUnsubscribePatients(() => unsubscribeFn);
        
      } catch (error) {
        console.error('❌ Error setting up patients data:', error);
        setLoadingPatients(false);
        setSnackbar({ 
          open: true, 
          message: 'Failed to connect to server', 
          severity: 'error' 
        });
      }
    };
    
    // Load available rooms for room assignment
    const loadAvailableRooms = async () => {
      try {
        setLoadingRooms(true);
        console.log('🏥 Loading available rooms from Firebase...');
        
        const roomsData = await DashboardService.getRooms();
        console.log('📋 Raw rooms data received:', roomsData);
        
        if (roomsData && roomsData.length > 0) {
          setAvailableRooms(roomsData);
          console.log('✅ Successfully loaded rooms:', roomsData.length);
          console.log('🔍 Room data sample:', roomsData[0]);
        } else {
          console.log('⚠️ No rooms found in Firebase, loading fallback data...');
          // Load fallback room data if Firebase is empty
          const fallbackRooms = [
            {
              id: 'room-101',
              roomNumber: '101',
              roomType: 'general',
              capacity: 2,
              currentOccupancy: 0,
              department: 'General Medicine',
              status: 'available' as const,
              availableBeds: [
                { bedNumber: 'A', isOccupied: false },
                { bedNumber: 'B', isOccupied: false }
              ]
            },
            {
              id: 'room-102',
              roomNumber: '102', 
              roomType: 'general',
              capacity: 2,
              currentOccupancy: 0,
              department: 'Cardiology',
              status: 'available' as const,
              availableBeds: [
                { bedNumber: 'A', isOccupied: false },
                { bedNumber: 'B', isOccupied: false }
              ]
            },
            {
              id: 'room-c1',
              roomNumber: 'C1',
              roomType: 'consultation',
              capacity: 1,
              currentOccupancy: 0,
              department: 'General',
              status: 'available' as const,
              availableBeds: [
                { bedNumber: '1', isOccupied: false }
              ]
            },
            {
              id: 'room-c2',
              roomNumber: 'C2',
              roomType: 'consultation',
              capacity: 1,
              currentOccupancy: 0,
              department: 'Orthopedics',
              status: 'available' as const,
              availableBeds: [
                { bedNumber: '1', isOccupied: false }
              ]
            },
            {
              id: 'room-p1',
              roomNumber: 'P1',
              roomType: 'procedure',
              capacity: 1,
              currentOccupancy: 0,
              department: 'Surgery',
              status: 'available' as const,
              availableBeds: [
                { bedNumber: '1', isOccupied: false }
              ]
            }
          ];
          setAvailableRooms(fallbackRooms);
          console.log('✅ Loaded fallback room data:', fallbackRooms.length);
        }
      } catch (error) {
        console.error('❌ Error loading rooms:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load room data. Using default rooms.',
          severity: 'warning'
        });
        
        // Set fallback rooms even on error
        const errorFallbackRooms = [
          {
            id: 'fallback-101',
            roomNumber: '101',
            roomType: 'general',
            capacity: 2,
            currentOccupancy: 0,
            department: 'General Medicine',
            status: 'available' as const,
            availableBeds: [
              { bedNumber: 'A', isOccupied: false },
              { bedNumber: 'B', isOccupied: false }
            ]
          },
          {
            id: 'fallback-c1',
            roomNumber: 'C1',
            roomType: 'consultation',
            capacity: 1,
            currentOccupancy: 0,
            department: 'General',
            status: 'available' as const,
            availableBeds: [
              { bedNumber: '1', isOccupied: false }
            ]
          }
        ];
        setAvailableRooms(errorFallbackRooms);
      } finally {
        setLoadingRooms(false);
      }
    };
    
    setupPatientsData();
    loadAvailableRooms();
    loadAvailableDoctors();
    
    // Cleanup function
    return () => {
      if (unsubscribePatients) {
        console.log('🧹 Cleaning up patients listener...');
        unsubscribePatients();
      }
    };
  }, []);
  
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Clear selectedPatient if it's no longer in the patients list
  useEffect(() => {
    if (selectedPatient && patients.length > 0) {
      const patientStillExists = patients.some(p => p.id === selectedPatient.id);
      if (!patientStillExists) {
        console.log('⚠️ Selected patient no longer exists, clearing selection');
        setSelectedPatient(null);
      }
    } else if (selectedPatient && patients.length === 0) {
      // No patients at all, clear selection
      console.log('⚠️ No patients in list, clearing selection');
      setSelectedPatient(null);
    }
  }, [patients, selectedPatient]);
  
  // Load data when switching tabs
  useEffect(() => {
    if (selectedPatient) {
      if (currentTab === 2) {
        // Payment Invoices tab - load payment data
        console.log('📊 Tab switched to Payment Invoices, loading payment data...');
        loadPaymentData(selectedPatient);
      } else if (currentTab === 3) {
        // Medical Reports tab - load reports data
        console.log('📄 Tab switched to Medical Reports, loading reports data...');
        loadMedicalReportsData(selectedPatient);
      }
    }
  }, [currentTab, selectedPatient?.id]);
  
  // Payment invoices state
  const [patientPayments, setPatientPayments] = useState<PatientPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    description: '',
    paymentMethod: 'Cash',
    paymentType: 'Consultation Fee',
    status: 'paid',
    transactionId: '',
    items: [{ name: '', description: '', quantity: 1, rate: 0, amount: 0 }],
    setTotalAmount: '' // For first payment or adjusting total
  });
  const [savingPayment, setSavingPayment] = useState(false);
  
  // Medical reports state
  const [medicalReports, setMedicalReports] = useState<MedicalReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Calculate stats from real patient data (matching React Native logic)
  const stats = useMemo(() => ({
    total: patients.length,
    inPatient: patients.filter(p => p.patientType === 'IP').length,
    outPatient: patients.filter(p => p.patientType === 'OP').length,
  }), [patients]);

  // Filter options (matching React Native)
  const filterOptions = [
    { label: 'All', value: 'All' },
    { label: 'IP Only', value: 'IP' },
    { label: 'OP Only', value: 'OP' },
  ];

  // Mock payment data (from React Native screen)
  const mockPaymentData: PatientPayment[] = [
    {
      id: 'INV-2024-001',
      patientId: 'KBR-OP-2025-9378237128',
      patientName: 'Shanker',
      date: '2024-01-15',
      time: '10:30 AM',
      amount: 1200,
      description: 'Consultation Fee - Dr. K. Ramesh',
      type: 'consultation',
      status: 'paid',
      paymentMethod: 'online',
      transactionId: 'TXN123456789',
      items: [
        { name: 'Doctor Consultation', amount: 800 },
        { name: 'Registration Fee', amount: 200 },
        { name: 'Medical Records', amount: 200 }
      ]
    },
    {
      id: 'INV-2024-002',
      patientId: 'KBR-OP-2025-9378237128',
      patientName: 'Shanker',
      date: '2024-01-16',
      time: '02:15 PM',
      amount: 2500,
      description: 'Laboratory Tests',
      type: 'tests',
      status: 'paid',
      paymentMethod: 'cash',
      transactionId: 'TXN123456790',
      items: [
        { name: 'Complete Blood Count', amount: 500 },
        { name: 'Lipid Profile', amount: 800 },
        { name: 'X-Ray Chest', amount: 600 },
        { name: 'ECG', amount: 400 },
        { name: 'Processing Fee', amount: 200 }
      ]
    },
    {
      id: 'INV-2024-003',
      patientId: 'KBR-OP-2025-9378237128',
      patientName: 'Shanker',
      date: '2024-01-18',
      time: '11:45 AM',
      amount: 15000,
      description: 'Room Charges & Medications',
      type: 'admission',
      status: 'paid',
      paymentMethod: 'card',
      transactionId: 'TXN123456791',
      items: [
        { name: 'Room Charges (3 days)', amount: 9000 },
        { name: 'Medications', amount: 3500 },
        { name: 'Nursing Care', amount: 2000 },
        { name: 'Service Charges', amount: 500 }
      ]
    },
    {
      id: 'INV-2024-004',
      patientId: 'KBR-OP-2025-9378237128',
      patientName: 'Shanker',
      date: '2024-01-20',
      time: '09:20 AM',
      amount: 800,
      description: 'Follow-up Consultation',
      type: 'consultation',
      status: 'pending',
      paymentMethod: 'cash',
      items: [
        { name: 'Follow-up Consultation', amount: 600 },
        { name: 'Prescription', amount: 200 }
      ]
    }
  ];

  // Filtered patients (matching React Native filtering logic)
  const filteredPatients = useMemo(() => {
    // First, deduplicate patients by ID to prevent duplicate keys
    const uniquePatients = patients.filter((patient, index, self) => 
      index === self.findIndex(p => p.id === patient.id)
    );
    
    // Then apply search, filter, and date range logic
    const filtered = uniquePatients.filter(patient => {
      const matchesSearch = patient.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           patient.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           patient.phone?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = selectedFilter === 'All' || patient.patientType === selectedFilter;
      
      // Date filter logic
      let matchesDate = true;
      if (fromDate || toDate) {
        const patientDate = patient.registrationDate || patient.admissionDate;
        if (patientDate) {
          try {
            const pDate = parseISO(patientDate);
            if (fromDate && toDate) {
              matchesDate = isWithinInterval(pDate, { start: startOfDay(fromDate), end: endOfDay(toDate) });
            } else if (fromDate) {
              matchesDate = pDate >= startOfDay(fromDate);
            } else if (toDate) {
              matchesDate = pDate <= endOfDay(toDate);
            }
          } catch (error) {
            console.error('Error parsing date:', error);
            matchesDate = false;
          }
        } else {
          matchesDate = false;
        }
      }
      
      return matchesSearch && matchesFilter && matchesDate;
    });
    
    return filtered;
  }, [patients, searchQuery, selectedFilter, fromDate, toDate]);

  // Calculate stats from filtered patient data
  const displayStats = useMemo(() => {
    const filteredStats = {
      total: filteredPatients.length,
      inPatient: filteredPatients.filter(p => p.patientType === 'IP').length,
      outPatient: filteredPatients.filter(p => p.patientType === 'OP').length,
    };
    
    // If no filter is applied, show original stats
    // If filter is applied, show filtered stats
    return selectedFilter === 'All' ? stats : filteredStats;
  }, [filteredPatients, stats, selectedFilter]);

  // Tab panel component
  const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`patient-tabpanel-${index}`}
        aria-labelledby={`patient-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
  };

  // Handle functions (matching React Native functionality with user feedback)
  const handleViewDetails = (patient: Patient) => {
    setSelectedPatient(patient);
    setCurrentTab(1); // Switch to Patient Details tab
    setSnackbar({ 
      open: true, 
      message: `Viewing details for ${patient.name}`, 
      severity: 'info' 
    });
  };

  // Separate function to load payment data without changing tabs
  const loadPaymentData = async (patient: Patient) => {
    setLoadingPayments(true);
    
    try {
      console.log('💰 Setting up real-time payment listeners for patient:', patient.id);
      
      let paymentsData: PatientPayment[] = [];
      let invoicesData: PatientPayment[] = [];
      let paymentsLoaded = false;
      let invoicesLoaded = false;
      
      const updateCombinedData = () => {
        if (paymentsLoaded && invoicesLoaded) {
          // ✅ FIX: Only show PAYMENTS in the transaction list (not invoices)
          // Since each payment generates an invoice, showing both causes confusion
          // The user can see payment details and download invoice separately
          const allTransactions = [...paymentsData].sort((a, b) => {
            const dateA = new Date(a.date + ' ' + a.time).getTime();
            const dateB = new Date(b.date + ' ' + b.time).getTime();
            return dateB - dateA;
          });
          
          console.log(`✅ Real-time update: ${allTransactions.length} payments loaded`);
          setPatientPayments(allTransactions);
          setLoadingPayments(false);
          
          if (allTransactions.length === 0) {
            console.log('⚠️ No payment records found');
          }
        }
      };
      
      // Set up real-time listener for payments collection
      const paymentsRef = collection(db, 'payments');
      const paymentsQuery = query(paymentsRef, where('patientId', '==', patient.id));
      
      const unsubscribePayments = onSnapshot(paymentsQuery, 
        (paymentsSnapshot) => {
          paymentsData = [];
          
          paymentsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const totalAmt = parseFloat(data.totalAmount || data.fullAmount || 0);
            const paidAmt = parseFloat(data.paidAmount || data.actualAmountPaid || 0);
            const amount = parseFloat(data.amount || 0);
            const dueAmt = Math.max(0, totalAmt - paidAmt);
            
            console.log('💾 Payment record from Firebase:', {
              id: doc.id,
              totalAmount: totalAmt,
              paidAmount: paidAmt,
              amount: amount,
              dueAmount: dueAmt,
              hasTransactionHistory: !!(data.transactionHistory && data.transactionHistory.length > 0)
            });
            
            // Only add ONE entry per payment record - NO duplicates
            const paymentDate = data.date || data.paymentDate || data.issueDate || data.lastPaymentDate || new Date().toISOString().split('T')[0];
            const paymentTime = data.time || data.paymentTime || new Date(data.createdAt?.toDate?.() || data.createdAt || Date.now()).toLocaleTimeString();
            
            paymentsData.push({
              id: doc.id,
              patientId: patient.id,
              patientName: patient.name,
              date: paymentDate,
              time: paymentTime,
              amount: amount,  // Individual payment amount (this transaction only)
              description: data.description || data.notes || 'Payment',
              type: (data.type || data.purpose || data.serviceType || 'consultation') as PatientPayment['type'],
              status: (data.status === 'completed' || data.status === 'paid' || data.status === 'Paid' || data.status === 'Fully Paid' ? 'paid' : 
                       data.status === 'pending' ? 'pending' : 
                       data.status === 'failed' ? 'failed' : 'paid') as PatientPayment['status'],
              paymentMethod: (data.paymentMethod || data.method || 'cash') as PatientPayment['paymentMethod'],
              transactionId: data.transactionId || data.razorpayPaymentId,
              items: data.items || [],
              totalAmount: totalAmt,  // Total bill amount (for calculation only)
              paidAmount: paidAmt,    // Cumulative paid (for calculation only)
              dueAmount: dueAmt,      // Due amount (for calculation only)
              source: 'payments' as const
            });
          });
          
          paymentsLoaded = true;
          console.log(`📊 Payments loaded: ${paymentsData.length} transactions`);
          updateCombinedData();
        },
        (error) => {
          console.error('❌ Error in payments listener:', error);
          paymentsLoaded = true;
          updateCombinedData();
        }
      );
      
      // Set up real-time listener for invoices collection
      const invoicesRef = collection(db, 'invoices');
      const invoicesQuery = query(invoicesRef, where('patientId', '==', patient.id));
      
      const unsubscribeInvoices = onSnapshot(invoicesQuery, 
        (invoicesSnapshot) => {
          invoicesData = invoicesSnapshot.docs.map(doc => {
            const data = doc.data();
            // ✅ NEW: Use invoice amount directly (not nested paymentDetails)
            const invoiceAmount = parseFloat(data.amount || data.totalAmount || 0);
            
            return {
              id: doc.id,
              patientId: patient.id,
              patientName: patient.name,
              date: data.issueDate || data.date || new Date().toISOString().split('T')[0],
              time: data.issueTime || new Date(data.createdAt?.toDate?.() || data.createdAt || Date.now()).toLocaleTimeString(),
              amount: invoiceAmount,  // ✅ Use invoice amount directly
              description: data.description || data.notes || 'Invoice',
              type: (data.serviceType || data.type || 'consultation') as PatientPayment['type'],
              status: (data.status === 'paid' ? 'paid' : 'pending') as PatientPayment['status'],
              paymentMethod: (data.paymentMethod || 'cash') as PatientPayment['paymentMethod'],
              transactionId: data.transactionId || data.invoiceNumber,
              items: data.items || [],
              // ✅ Don't pass cumulative fields from invoices
              totalAmount: undefined,
              paidAmount: undefined,
              dueAmount: undefined,
              source: 'invoices' as const
            };
          });
          invoicesLoaded = true;
          console.log(`📊 Invoices loaded: ${invoicesData.length}`);
          updateCombinedData();
        },
        (error) => {
          console.error('❌ Error in invoices listener:', error);
          invoicesLoaded = true;
          updateCombinedData();
        }
      );
      
      // Store unsubscribe functions to clean up later
      return () => {
        unsubscribePayments();
        unsubscribeInvoices();
      };
      
    } catch (error) {
      console.error('❌ Error setting up payment listeners:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to load payment data', 
        severity: 'error' 
      });
      setPatientPayments([]);
      setLoadingPayments(false);
    }
  };

  const handleViewPaymentInvoices = async (patient: Patient) => {
    setSelectedPatient(patient);
    setCurrentTab(2); // Switch to Payment Invoices tab
    setSnackbar({ 
      open: true, 
      message: `Loading payment transactions for ${patient.name}...`, 
      severity: 'info' 
    });
    // Data will be loaded by the useEffect watching currentTab
  };

  const handleViewMedicalReports = async (patient: Patient) => {
    setSelectedPatient(patient);
    setCurrentTab(3); // Switch to Medical Reports tab
    setSnackbar({ 
      open: true, 
      message: `Loading medical reports for ${patient.name}...`, 
      severity: 'info' 
    });
    // Data will be loaded by the useEffect watching currentTab
  };
  
  // Actual data loading function for medical reports
  const loadMedicalReportsData = async (patient: Patient) => {
    setLoadingReports(true);
    
    try {
      // Fetch reports from both medicalReports and adminReports collections
      console.log('📋 Fetching medical reports for patient:', patient.id, 'Phone:', patient.phone);
      
      // Query medicalReports collection
      const medicalReportsRef = collection(db, 'medicalReports');
      const medicalReportsQuery = query(medicalReportsRef, where('patientId', '==', patient.id));
      const medicalReportsSnapshot = await getDocs(medicalReportsQuery);
      
      const medicalReports: MedicalReport[] = medicalReportsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          patientId: patient.id,
          patientName: patient.name,
          reportType: data.reportType || data.testType || 'General Report',
          date: data.date || new Date().toISOString().split('T')[0],
          doctor: data.doctor || patient.doctor,
          department: data.department || patient.department || 'General',
          status: data.status || 'completed',
          findings: data.findings || data.results,
          recommendations: data.recommendations,
          attachments: data.attachments || data.fileURLs || []
        };
      });
      
      // Query adminReports collection (for IP/OP patients)
      const adminReportsRef = collection(db, 'adminReports');
      const adminReportsQuery = query(adminReportsRef, where('patientId', '==', patient.id));
      const adminReportsSnapshot = await getDocs(adminReportsQuery);
      
      const adminReports: MedicalReport[] = adminReportsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          patientId: patient.id,
          patientName: patient.name,
          reportType: data.reportName || data.category || 'Admin Report',
          date: data.createdAt?.toDate?.()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
          doctor: 'Admin',
          department: patient.department || 'General',
          status: 'completed',
          findings: data.notes || '',
          recommendations: '',
          attachments: data.fileUrls || []
        };
      });
      
      // Combine both report types
      const allReports = [...medicalReports, ...adminReports];
      
      console.log(`✅ Loaded ${medicalReports.length} medical reports and ${adminReports.length} admin reports for ${patient.name} (Phone: ${patient.phone})`);
      setMedicalReports(allReports);
      
      if (allReports.length === 0) {
        setSnackbar({ 
          open: true, 
          message: `No medical reports found for ${patient.name}`, 
          severity: 'info' 
        });
      } else {
        console.log(`📊 Report details for ${patient.name}:`, allReports.map(r => ({
          id: r.id,
          type: r.reportType,
          date: r.date,
          attachments: r.attachments?.length || 0
        })));
      }
    } catch (error) {
      console.error('❌ Error loading medical reports:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to load medical reports', 
        severity: 'error' 
      });
      setMedicalReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  // Invoice action handlers
  const handleViewInvoice = (payment: PatientPayment) => {
    // Create a detailed invoice view in a new window or modal
    const invoiceWindow = window.open('', '_blank', 'width=800,height=600');
    if (invoiceWindow) {
      const invoiceHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice - ${payment.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .info { margin: 20px 0; }
            .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
            .items { margin: 20px 0; }
            .items table { width: 100%; border-collapse: collapse; }
            .items th, .items td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .items th { background-color: #f2f2f2; }
            .total { text-align: right; font-size: 18px; font-weight: bold; margin-top: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>KBR LIFE CARE</h1>
            <h2>Payment Invoice</h2>
          </div>
          <div class="info">
            <div class="info-row"><strong>Invoice ID:</strong> <span>${payment.id}</span></div>
            <div class="info-row"><strong>Patient Name:</strong> <span>${payment.patientName}</span></div>
            <div class="info-row"><strong>Date:</strong> <span>${payment.date} ${payment.time}</span></div>
            <div class="info-row"><strong>Transaction ID:</strong> <span>${payment.transactionId || 'N/A'}</span></div>
            <div class="info-row"><strong>Payment Method:</strong> <span>${payment.paymentMethod.toUpperCase()}</span></div>
            <div class="info-row"><strong>Status:</strong> <span>${payment.status.toUpperCase()}</span></div>
          </div>
          <div class="items">
            <h3>Invoice Details</h3>
            ${payment.items && payment.items.length > 0 ? `
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${payment.items.map(item => `
                    <tr>
                      <td>${item.name}</td>
                      <td>₹${item.amount.toLocaleString()}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : `<p>${payment.description}</p>`}
          </div>
          <div class="total">
            Total Amount: ₹${payment.amount.toLocaleString()}
          </div>
        </body>
        </html>
      `;
      invoiceWindow.document.write(invoiceHTML);
      invoiceWindow.document.close();
    }
  };

  const handleShareInvoice = async (payment: PatientPayment) => {
    const invoiceText = `
KBR LIFE CARE - Payment Invoice
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Invoice ID: ${payment.id}
Patient: ${payment.patientName}
Date: ${payment.date} ${payment.time}
Amount: ₹${payment.amount.toLocaleString()}
Payment Method: ${payment.paymentMethod.toUpperCase()}
Status: ${payment.status.toUpperCase()}
${payment.transactionId ? `Transaction ID: ${payment.transactionId}` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `.trim();

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invoice - ${payment.id}`,
          text: invoiceText
        });
        setSnackbar({ 
          open: true, 
          message: 'Invoice shared successfully', 
          severity: 'success' 
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
          setSnackbar({ 
            open: true, 
            message: 'Failed to share invoice', 
            severity: 'error' 
          });
        }
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(invoiceText);
        setSnackbar({ 
          open: true, 
          message: 'Invoice copied to clipboard', 
          severity: 'success' 
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to copy invoice', 
          severity: 'error' 
        });
      }
    }
  };

  const handleDownloadInvoice = (payment: PatientPayment) => {
    // Create invoice HTML content
    const invoiceHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>Invoice - ${payment.id}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
    .info { margin: 20px 0; }
    .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
    .items { margin: 20px 0; }
    .items table { width: 100%; border-collapse: collapse; }
    .items th, .items td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    .items th { background-color: #f2f2f2; }
    .total { text-align: right; font-size: 18px; font-weight: bold; margin-top: 20px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>KBR LIFE CARE</h1>
    <h2>Payment Invoice</h2>
  </div>
  <div class="info">
    <div class="info-row"><strong>Invoice ID:</strong> <span>${payment.id}</span></div>
    <div class="info-row"><strong>Patient Name:</strong> <span>${payment.patientName}</span></div>
    <div class="info-row"><strong>Date:</strong> <span>${payment.date} ${payment.time}</span></div>
    <div class="info-row"><strong>Transaction ID:</strong> <span>${payment.transactionId || 'N/A'}</span></div>
    <div class="info-row"><strong>Payment Method:</strong> <span>${payment.paymentMethod.toUpperCase()}</span></div>
    <div class="info-row"><strong>Status:</strong> <span>${payment.status.toUpperCase()}</span></div>
  </div>
  <div class="items">
    <h3>Invoice Details</h3>
    ${payment.items && payment.items.length > 0 ? `
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          ${payment.items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td>₹${item.amount.toLocaleString()}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    ` : `<p>${payment.description}</p>`}
  </div>
  <div class="total">
    Total Amount: ₹${payment.amount.toLocaleString()}
  </div>
</body>
</html>
    `.trim();

    // Create a Blob from the HTML content
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `Invoice_${payment.patientName.replace(/\s+/g, '_')}_${payment.id}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setSnackbar({ 
      open: true, 
      message: 'Invoice downloaded successfully', 
      severity: 'success' 
    });
  };

  // Medical Report action handlers
  const handleViewReport = (report: MedicalReport) => {
    console.log('👁️ Viewing report:', report);
    
    if (report.attachments && report.attachments.length > 0) {
      // Open first attachment in new tab
      window.open(report.attachments[0], '_blank');
    } else {
      // Show report details in a modal/alert
      const reportDetails = `
Report Type: ${report.reportType}
Patient: ${report.patientName}
Date: ${report.date}
Doctor: Dr. ${report.doctor}
Department: ${report.department}
Status: ${report.status}
${report.findings ? `\nFindings:\n${report.findings}` : ''}
${report.recommendations ? `\nRecommendations:\n${report.recommendations}` : ''}
      `.trim();
      
      alert(reportDetails);
    }
  };

  const handlePrintReport = (report: MedicalReport) => {
    console.log('🖨️ Printing report:', report);
    
    // If report has attachments, open them for printing
    if (report.attachments && report.attachments.length > 0) {
      console.log('📎 Opening attached files for printing...');
      setSnackbar({ 
        open: true, 
        message: `Opening ${report.attachments.length} file(s) for printing...`, 
        severity: 'info' 
      });
      
      // Open each attachment in a new window
      report.attachments.forEach((url, index) => {
        setTimeout(() => {
          const printWindow = window.open(url, '_blank');
          if (printWindow) {
            // Wait for content to load, then trigger print
            printWindow.onload = () => {
              setTimeout(() => {
                printWindow.print();
              }, 500);
            };
          }
        }, index * 1000); // Delay each window by 1 second
      });
      
      return;
    }
    
    // If no attachments, create printable HTML content
    const printHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>Medical Report - ${report.reportType}</title>
  <style>
    @media print {
      body { margin: 0; padding: 20px; }
      .no-print { display: none; }
    }
    body { 
      font-family: Arial, sans-serif; 
      padding: 40px;
      max-width: 800px;
      margin: 0 auto;
    }
    .header { 
      text-align: center; 
      border-bottom: 3px solid #10B981; 
      padding-bottom: 20px; 
      margin-bottom: 30px; 
    }
    .header h1 { margin: 0; color: #10B981; font-size: 28px; }
    .header h2 { margin: 10px 0 0 0; color: #666; font-size: 20px; }
    .info { 
      background: #F8FAFC; 
      padding: 20px; 
      border-radius: 8px; 
      margin-bottom: 20px; 
    }
    .info-row { 
      display: flex; 
      justify-content: space-between; 
      margin: 10px 0; 
      padding: 8px 0;
      border-bottom: 1px solid #E5E7EB;
    }
    .info-row:last-child { border-bottom: none; }
    .info-row strong { color: #374151; }
    .info-row span { color: #6B7280; }
    .section { 
      margin: 25px 0; 
      padding: 20px;
      border-left: 4px solid #10B981;
      background: #F0FDF4;
    }
    .section h3 { 
      margin: 0 0 15px 0; 
      color: #10B981; 
      font-size: 18px;
    }
    .section p { 
      margin: 0; 
      color: #374151; 
      line-height: 1.6;
      white-space: pre-wrap;
    }
    .status { 
      display: inline-block;
      padding: 6px 16px;
      border-radius: 20px;
      font-weight: bold;
      font-size: 12px;
      text-transform: uppercase;
    }
    .status.completed { background: #10B981; color: white; }
    .status.pending { background: #F59E0B; color: white; }
    .status.in-progress { background: #3B82F6; color: white; }
    .attachments {
      margin: 20px 0;
      padding: 15px;
      background: #FEF3C7;
      border-radius: 8px;
    }
    .attachments h3 {
      margin: 0 0 10px 0;
      color: #F59E0B;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 2px solid #E5E7EB;
      text-align: center;
      color: #9CA3AF;
      font-size: 12px;
    }
    .print-button {
      margin: 20px 0;
      text-align: center;
    }
    @media print {
      .print-button { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>KBR LIFE CARE</h1>
    <h2>Medical Report</h2>
  </div>
  
  <div class="info">
    <div class="info-row">
      <strong>Report Type:</strong> 
      <span>${report.reportType}</span>
    </div>
    <div class="info-row">
      <strong>Patient Name:</strong> 
      <span>${report.patientName}</span>
    </div>
    <div class="info-row">
      <strong>Patient ID:</strong> 
      <span>${report.patientId}</span>
    </div>
    <div class="info-row">
      <strong>Date:</strong> 
      <span>${report.date}</span>
    </div>
    <div class="info-row">
      <strong>Doctor:</strong> 
      <span>Dr. ${report.doctor}</span>
    </div>
    <div class="info-row">
      <strong>Department:</strong> 
      <span>${report.department}</span>
    </div>
    <div class="info-row">
      <strong>Status:</strong> 
      <span class="status ${report.status}">${report.status.toUpperCase()}</span>
    </div>
  </div>

  ${report.findings ? `
    <div class="section">
      <h3>Findings</h3>
      <p>${report.findings}</p>
    </div>
  ` : ''}

  ${report.recommendations ? `
    <div class="section">
      <h3>Recommendations</h3>
      <p>${report.recommendations}</p>
    </div>
  ` : ''}

  ${report.attachments && report.attachments.length > 0 ? `
    <div class="attachments">
      <h3>Attachments (${report.attachments.length})</h3>
      <p>Report includes ${report.attachments.length} attached file(s)</p>
    </div>
  ` : ''}

  <div class="footer">
    <p>Generated on ${new Date().toLocaleString()}</p>
    <p>KBR Life Care - Hospital Management System</p>
  </div>

  <div class="print-button no-print">
    <button onclick="window.print()" style="
      padding: 12px 24px;
      background: #10B981;
      color: white;
      border: none;
      border-radius: 6px;
      font-size: 16px;
      cursor: pointer;
      font-weight: bold;
    ">Print Report</button>
  </div>
</body>
</html>
    `.trim();

    // Open print window
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
      printWindow.focus();
    } else {
      setSnackbar({ 
        open: true, 
        message: 'Please allow pop-ups to print reports', 
        severity: 'error' 
      });
    }
  };

  const handleShareReport = async (report: MedicalReport) => {
    console.log('📤 Sharing report:', report);
    
    // Build report text with attachment links
    let reportText = `
KBR LIFE CARE - Medical Report
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Report Type: ${report.reportType}
Patient: ${report.patientName}
Patient ID: ${report.patientId}
Date: ${report.date}
Doctor: Dr. ${report.doctor}
Department: ${report.department}
Status: ${report.status.toUpperCase()}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${report.findings ? `\nFindings:\n${report.findings}\n` : ''}
${report.recommendations ? `\nRecommendations:\n${report.recommendations}\n` : ''}`;

    // Add attachment URLs to share
    if (report.attachments && report.attachments.length > 0) {
      reportText += `\n\nAttached Files (${report.attachments.length}):\n`;
      report.attachments.forEach((url, index) => {
        reportText += `\nFile ${index + 1}: ${url}`;
      });
    }

    reportText = reportText.trim();

    if (navigator.share) {
      try {
        // Try to share with URLs
        const shareData: any = {
          title: `Medical Report - ${report.reportType}`,
          text: reportText
        };
        
        // If there's only one attachment, try to include the URL
        if (report.attachments && report.attachments.length === 1) {
          shareData.url = report.attachments[0];
        }
        
        await navigator.share(shareData);
        setSnackbar({ 
          open: true, 
          message: 'Report shared successfully', 
          severity: 'success' 
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
          // Fallback to clipboard
          try {
            await navigator.clipboard.writeText(reportText);
            setSnackbar({ 
              open: true, 
              message: 'Report details and file links copied to clipboard', 
              severity: 'success' 
            });
          } catch (clipError) {
            setSnackbar({ 
              open: true, 
              message: 'Failed to share report', 
              severity: 'error' 
            });
          }
        }
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(reportText);
        setSnackbar({ 
          open: true, 
          message: 'Report details and file links copied to clipboard', 
          severity: 'success' 
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to copy report', 
          severity: 'error' 
        });
      }
    }
  };

  const handleDownloadReport = async (report: MedicalReport) => {
    console.log('💾 Downloading report:', report);
    
    if (report.attachments && report.attachments.length > 0) {
      // Download all attachments
      setSnackbar({ 
        open: true, 
        message: `Downloading ${report.attachments.length} file(s)...`, 
        severity: 'info' 
      });
      
      for (let i = 0; i < report.attachments.length; i++) {
        const url = report.attachments[i];
        try {
          // Open in new tab for download
          window.open(url, '_blank');
          await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
        } catch (error) {
          console.error('Error downloading file:', error);
        }
      }
      
      setSnackbar({ 
        open: true, 
        message: 'Files downloaded successfully', 
        severity: 'success' 
      });
    } else {
      // Generate and download HTML report
      const reportHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>Medical Report - ${report.reportType}</title>
  <meta charset="UTF-8">
  <style>
    body { 
      font-family: Arial, sans-serif; 
      padding: 40px;
      max-width: 800px;
      margin: 0 auto;
      background: white;
    }
    .header { 
      text-align: center; 
      border-bottom: 3px solid #10B981; 
      padding-bottom: 20px; 
      margin-bottom: 30px; 
    }
    .header h1 { margin: 0; color: #10B981; font-size: 28px; }
    .header h2 { margin: 10px 0 0 0; color: #666; font-size: 20px; }
    .info { 
      background: #F8FAFC; 
      padding: 20px; 
      border-radius: 8px; 
      margin-bottom: 20px; 
    }
    .info-row { 
      display: flex; 
      justify-content: space-between; 
      margin: 10px 0; 
      padding: 8px 0;
      border-bottom: 1px solid #E5E7EB;
    }
    .info-row:last-child { border-bottom: none; }
    .info-row strong { color: #374151; }
    .info-row span { color: #6B7280; }
    .section { 
      margin: 25px 0; 
      padding: 20px;
      border-left: 4px solid #10B981;
      background: #F0FDF4;
    }
    .section h3 { 
      margin: 0 0 15px 0; 
      color: #10B981; 
      font-size: 18px;
    }
    .section p { 
      margin: 0; 
      color: #374151; 
      line-height: 1.6;
      white-space: pre-wrap;
    }
    .status { 
      display: inline-block;
      padding: 6px 16px;
      border-radius: 20px;
      font-weight: bold;
      font-size: 12px;
      text-transform: uppercase;
    }
    .status.completed { background: #10B981; color: white; }
    .status.pending { background: #F59E0B; color: white; }
    .status.in-progress { background: #3B82F6; color: white; }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 2px solid #E5E7EB;
      text-align: center;
      color: #9CA3AF;
      font-size: 12px;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>KBR LIFE CARE</h1>
    <h2>Medical Report</h2>
  </div>
  
  <div class="info">
    <div class="info-row">
      <strong>Report Type:</strong> 
      <span>${report.reportType}</span>
    </div>
    <div class="info-row">
      <strong>Patient Name:</strong> 
      <span>${report.patientName}</span>
    </div>
    <div class="info-row">
      <strong>Patient ID:</strong> 
      <span>${report.patientId}</span>
    </div>
    <div class="info-row">
      <strong>Date:</strong> 
      <span>${report.date}</span>
    </div>
    <div class="info-row">
      <strong>Doctor:</strong> 
      <span>Dr. ${report.doctor}</span>
    </div>
    <div class="info-row">
      <strong>Department:</strong> 
      <span>${report.department}</span>
    </div>
    <div class="info-row">
      <strong>Status:</strong> 
      <span class="status ${report.status}">${report.status.toUpperCase()}</span>
    </div>
  </div>

  ${report.findings ? `
    <div class="section">
      <h3>Findings</h3>
      <p>${report.findings}</p>
    </div>
  ` : ''}

  ${report.recommendations ? `
    <div class="section">
      <h3>Recommendations</h3>
      <p>${report.recommendations}</p>
    </div>
  ` : ''}

  <div class="footer">
    <p>Generated on ${new Date().toLocaleString()}</p>
    <p>KBR Life Care - Hospital Management System</p>
  </div>
</body>
</html>
      `.trim();

      // Create blob and download
      const blob = new Blob([reportHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Medical_Report_${report.patientName.replace(/\s+/g, '_')}_${report.date}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({ 
        open: true, 
        message: 'Report downloaded successfully', 
        severity: 'success' 
      });
    }
  };

  // Function to reset registration form and close modal
  const handleCloseRegistrationModal = () => {
    setShowRegistrationModal(false);
    // Reset form to initial state
    setRegistrationForm({
      patientType: 'OP',
      fullName: '',
      age: '',
      gender: '',
      bloodGroup: '',
      phoneNumber: '',
      emergencyContact: '',
      address: '',
      doctor: '',
      department: '',
      roomNumber: '',
      bedNumber: '',
      referredBy: '',
      totalAmount: '',
      initialPayment: '',
      paymentMethod: 'Cash',
      paymentType: 'Registration Fee',
      symptoms: '',
      allergies: '',
      transactionId: '',
      razorpayPaymentId: '',
      razorpayOrderId: '',
      razorpaySignature: '',
      editTotalAmount: '',
      editPaidAmount: '',
      editDueAmount: '',
      billChangeReason: ''
    });
    
    // Reset payment state
    setProcessingPayment(false);
    setPaymentCompleted(false);
    setRazorpayOrderId('');
  };

  const handleRegisterNewPatient = async () => {
    // Always refresh room data BEFORE opening modal to show latest bed availability
    console.log('🔄 Refreshing room data before opening modal...');
    await loadAvailableRooms();
    console.log('📊 Available rooms loaded:', availableRooms.length);
    
    setShowRegistrationModal(true);
    setSnackbar({ 
      open: true, 
      message: 'Opening patient registration form with latest room availability...', 
      severity: 'info' 
    });
  };

  // Function to manually refresh rooms
  const loadAvailableRooms = async () => {
    try {
      setLoadingRooms(true);
        console.log('🏥 Loading available rooms from Firebase...');

        const roomsData = await DashboardService.getRooms();
        console.log('📋 Raw rooms data received:', roomsData);
        
        if (roomsData && roomsData.length > 0) {
        // Transform and validate room data
        const transformedRooms = roomsData.map((room: any) => {
          // Ensure availableBeds is properly structured
          const beds = room.availableBeds || [];
          const formattedBeds = beds.map((bed: any) => {
            if (typeof bed === 'string') {
              return { bedNumber: bed, isOccupied: false };
            }
            return bed;
          });
          
          // Room beds formatted for display
          
          return {
            ...room,
            availableBeds: formattedBeds
          };
        });
        
        setAvailableRooms(transformedRooms);
        console.log('✅ Successfully loaded and transformed rooms:', transformedRooms.length);
        console.log('🔍 Room data sample:', transformedRooms[0]);
      } else {
        console.log('⚠️ No rooms found in Firebase, loading fallback data...');
        // Load fallback room data if Firebase is empty
        const fallbackRooms = [
          {
            id: 'room-101',
            roomNumber: '101',
            roomType: 'general',
            capacity: 2,
            currentOccupancy: 0,
            department: 'General Medicine',
            status: 'available' as const,
            availableBeds: [
              { bedNumber: 'A', isOccupied: false },
              { bedNumber: 'B', isOccupied: false }
            ]
          },
          {
            id: 'room-102',
            roomNumber: '102', 
            roomType: 'general',
            capacity: 2,
            currentOccupancy: 0,
            department: 'Cardiology',
            status: 'available' as const,
            availableBeds: [
              { bedNumber: 'A', isOccupied: false },
              { bedNumber: 'B', isOccupied: false }
            ]
          },
          {
            id: 'room-c1',
            roomNumber: 'C1',
            roomType: 'consultation',
            capacity: 1,
            currentOccupancy: 0,
            department: 'General',
            status: 'available' as const,
            availableBeds: [
              { bedNumber: '1', isOccupied: false }
            ]
          },
          {
            id: 'room-c2',
            roomNumber: 'C2',
            roomType: 'consultation',
            capacity: 1,
            currentOccupancy: 0,
            department: 'Orthopedics',
            status: 'available' as const,
            availableBeds: [
              { bedNumber: '1', isOccupied: false }
            ]
          },
          {
            id: 'room-p1',
            roomNumber: 'P1',
            roomType: 'procedure',
            capacity: 1,
            currentOccupancy: 0,
            department: 'Surgery',
            status: 'available' as const,
            availableBeds: [
              { bedNumber: '1', isOccupied: false }
            ]
          }
        ];
        setAvailableRooms(fallbackRooms);
        console.log('✅ Loaded fallback room data:', fallbackRooms.length);
      }
    } catch (error) {
      console.error('❌ Error loading rooms:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load room data. Using default rooms.',
        severity: 'warning'
      });
      
      // Set fallback rooms even on error
      const errorFallbackRooms = [
        {
          id: 'fallback-101',
          roomNumber: '101',
          roomType: 'general',
          capacity: 2,
          currentOccupancy: 0,
          department: 'General Medicine',
          status: 'available' as const,
          availableBeds: [
            { bedNumber: 'A', isOccupied: false },
            { bedNumber: 'B', isOccupied: false }
          ]
        },
        {
          id: 'fallback-c1',
          roomNumber: 'C1',
          roomType: 'consultation',
          capacity: 1,
          currentOccupancy: 0,
          department: 'General',
          status: 'available' as const,
          availableBeds: [
            { bedNumber: '1', isOccupied: false }
          ]
        }
      ];
      setAvailableRooms(errorFallbackRooms);
    } finally {
      setLoadingRooms(false);
    }
  };

  // Load available doctors from Firebase backend
  const loadAvailableDoctors = async () => {
    console.log('🩺 Starting doctor load from Firebase backend...');
    setLoadingDoctors(true);
    try {
      console.log('📡 Fetching doctors from Firebase DashboardService...');
      const doctorsData = await DashboardService.getDoctors();
      
      // Validate data
      if (!doctorsData || !Array.isArray(doctorsData)) {
        console.warn('⚠️ Invalid doctors data from Firebase');
        setAvailableDoctors([]);
        setLoadingDoctors(false);
        return;
      }
      
      console.log('✅ Firebase doctors successfully loaded:', doctorsData);
      console.log('📊 Total doctors from Firebase:', doctorsData.length);
      
      // Transform Firebase data to ensure consistent structure
      const transformedDoctors = doctorsData.map((doctor: any) => ({
        id: doctor.id,
        name: doctor.name || `Dr. ${doctor.firstName || ''} ${doctor.lastName || ''}`.trim(),
        specialization: doctor.specialization || doctor.specialty || doctor.department,
        department: doctor.department || doctor.specialization || 'General Medicine',
        consultationFee: doctor.consultationFee || doctor.fee || 500,
        isActive: doctor.isActive !== false,
        status: doctor.status || 'Active'
      }));
      
      console.log('🔄 Transformed doctors:', transformedDoctors);
      setAvailableDoctors(transformedDoctors);
      
    } catch (error) {
      console.error('❌ Error loading doctors from Firebase:', error);
      setAvailableDoctors([]);
      setSnackbar({
        open: true,
        message: 'Failed to load doctors list',
        severity: 'error'
      });
    } finally {
      setLoadingDoctors(false);
      console.log('🏁 Doctor loading process completed');
    }
  };

  const handleRegistrationSuccess = async (newPatient: Patient) => {
    setSnackbar({ open: true, message: `Patient ${newPatient.name} registered successfully!`, severity: 'success' });
    await handleRefresh();
  };

  // Handle actual patient registration
  const handlePatientRegistration = async () => {
    try {
      console.log('📝 Starting patient registration...');
      console.log('Registration Form Data:', registrationForm);
      console.log('Payment Completed Status:', paymentCompleted);
      console.log('Processing Payment Status:', processingPayment);
      
      // Authentication check
      if (!auth.currentUser) {
        console.error('❌ No authenticated user found');
        setSnackbar({ 
          open: true, 
          message: 'Authentication error. Please refresh the page and login again.', 
          severity: 'error' 
        });
        setRegistrationCompleting(false);
        return;
      }
      
      console.log('✅ Authenticated user:', auth.currentUser.email);
      
      // CRITICAL: Prevent re-triggering payment if already in progress or completed
      // Check if payment method is Razorpay and handle payment first
      if (registrationForm.paymentMethod === 'Razorpay') {
        if (!paymentCompleted && !processingPayment) {
          console.log('💳 Razorpay selected but payment not completed, initiating payment...');
          await handlePatientRazorpayPayment();
          return; // Exit here, payment success will continue registration
        } else if (processingPayment) {
          console.log('⚠️ Payment already in progress, skipping duplicate request');
          return; // Prevent duplicate payment processing
        } else {
          console.log('✅ Razorpay payment already completed, proceeding with registration');
        }
      }
      
      // Set completing state to prevent UI confusion
      setRegistrationCompleting(true);
      
      // Validate required fields
      if (!registrationForm.fullName || !registrationForm.age || !registrationForm.gender || 
          !registrationForm.phoneNumber || !registrationForm.doctor || !registrationForm.department ||
          !registrationForm.totalAmount) {
        setSnackbar({ 
          open: true, 
          message: 'Please fill in all required fields', 
          severity: 'error' 
        });
        return;
      }

      // Phone number validation - must be exactly 10 digits
      if (registrationForm.phoneNumber.length !== 10) {
        setSnackbar({ 
          open: true, 
          message: 'Phone number must be exactly 10 digits', 
          severity: 'error' 
        });
        return;
      }
      if (!/^[6-9]\d{9}$/.test(registrationForm.phoneNumber)) {
        setSnackbar({ 
          open: true, 
          message: 'Invalid phone number format', 
          severity: 'error' 
        });
        return;
      }
      
      // Emergency contact validation if provided (for IP patients it's required)
      if (registrationForm.emergencyContact) {
        if (registrationForm.emergencyContact.length !== 10) {
          setSnackbar({ 
            open: true, 
            message: 'Emergency contact must be exactly 10 digits', 
            severity: 'error' 
          });
          return;
        }
      }

      // Validate room and bed for both IP and OP patients (required in current form)
      if (!registrationForm.roomNumber || !registrationForm.bedNumber) {
        setSnackbar({ 
          open: true, 
          message: 'Room and bed numbers are required', 
          severity: 'error' 
        });
        return;
      }

      // For IP patients, validate additional required fields
      if (registrationForm.patientType === 'IP') {
        if (!registrationForm.emergencyContact) {
          setSnackbar({ 
            open: true, 
            message: 'Emergency contact is required for IP patients', 
            severity: 'error' 
          });
          return;
        }
        
        if (!registrationForm.allergies) {
          setSnackbar({ 
            open: true, 
            message: 'Medical history and allergies are required for IP patients', 
            severity: 'error' 
          });
          return;
        }
      }

      console.log('✅ Validation passed, creating patient data...');

      // Add patient to Firebase directly
      const totalAmountValue = parseFloat(registrationForm.totalAmount) || 0;
      const initialPaymentValue = parseFloat(registrationForm.initialPayment) || 0;
      
      const patientData = {
        name: registrationForm.fullName,
        fullName: registrationForm.fullName,
        age: parseInt(registrationForm.age),
        gender: registrationForm.gender,
        bloodGroup: registrationForm.bloodGroup || '',
        phone: registrationForm.phoneNumber,
        phoneNumber: registrationForm.phoneNumber,
        emergencyContact: registrationForm.emergencyContact || '',
        address: registrationForm.address || '',
        doctor: registrationForm.doctor,
        department: registrationForm.department,
        patientType: registrationForm.patientType,
        room: registrationForm.roomNumber,
        roomNumber: registrationForm.roomNumber,
        bedNo: registrationForm.bedNumber,
        bedNumber: registrationForm.bedNumber,
        referredBy: registrationForm.referredBy || '',
        symptoms: registrationForm.symptoms || '',
        allergies: registrationForm.allergies || '',
        paymentMethod: registrationForm.paymentMethod,
        paymentType: registrationForm.paymentType,
        totalAmount: totalAmountValue,
        paidAmount: initialPaymentValue,
        dueAmount: Math.max(0, totalAmountValue - initialPaymentValue),
        billChangeHistory: [],
        initialPayment: initialPaymentValue,
        registrationDate: new Date().toISOString(),
        admissionDate: registrationForm.patientType === 'IP' ? new Date().toISOString() : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        isActive: true,
        // Razorpay payment details (if applicable)
        ...(registrationForm.paymentMethod === 'Razorpay' && {
          transactionId: registrationForm.transactionId,
          razorpayPaymentId: registrationForm.razorpayPaymentId,
          razorpayOrderId: registrationForm.razorpayOrderId,
          razorpaySignature: registrationForm.razorpaySignature,
          paymentStatus: paymentCompleted ? 'paid' : 'pending'
        })
      };

      console.log('📤 Saving patient to Firebase...', patientData);
      const docRef = await addDoc(collection(db, 'patients'), patientData);
      
      console.log('✅ Patient registered with ID:', docRef.id);
      
      // Define payment variables for invoice generation
      const totalAmount = parseFloat(registrationForm.totalAmount) || 0;
      const initialPayment = parseFloat(registrationForm.initialPayment || '0') || 0;
      
      // ✅ FIX: Create payment record AND invoice if there's an actual payment (initialPayment > 0)
      // This ensures consultation payment appears in Payments page
      if (totalAmount > 0 && initialPayment > 0) {
        try {
          console.log('💰 [PatientRegistration] Creating payment record for initial payment:', initialPayment);
          
          // ✅ Create payment record in payments collection
          const paymentRecord = {
            paymentId: `PAY-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            patientId: docRef.id,
            patientName: registrationForm.fullName,
            
            // THIS payment only (NOT cumulative)
            amount: initialPayment,
            
            paymentDate: new Date().toISOString().split('T')[0],
            paymentTime: new Date().toLocaleTimeString(),
            
            // Payment method details
            paymentMethod: registrationForm.paymentMethod.toLowerCase(),
            // ✅ Only add transactionId for Razorpay payments, not for Cash
            ...(registrationForm.paymentMethod.toLowerCase() === 'razorpay' && registrationForm.transactionId ? { transactionId: registrationForm.transactionId } : {}),
            
            // Payment purpose
            purpose: registrationForm.paymentType.toLowerCase().includes('consultation') ? 'consultation' : 
                     registrationForm.paymentType.toLowerCase().includes('registration') ? 'registration' : 'consultation',
            
            description: `${registrationForm.paymentType} payment during patient registration`,
            
            // Payment status
            status: 'paid',
            
            // Line items
            items: [{
              name: registrationForm.paymentType,
              description: `${registrationForm.patientType} patient - ${registrationForm.department}`,
              quantity: 1,
              rate: initialPayment,
              amount: initialPayment
            }],
            
            // Metadata
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          };

          console.log('💾 [PatientRegistration] Saving payment to Firebase:', paymentRecord.paymentId);
          const paymentDoc = await addDoc(collection(db, 'payments'), paymentRecord);
          console.log('✅ [PatientRegistration] Payment saved successfully:', paymentDoc.id);

          // ✅ Generate invoice for this payment
          console.log('🧾 [PatientRegistration] Generating invoice for payment...');
          await generateInvoiceForPayment(paymentDoc.id, paymentRecord, db);
          console.log('✅ [PatientRegistration] Payment record and invoice created successfully');
          
        } catch (paymentError) {
          console.error('❌ [PatientRegistration] Error creating payment/invoice:', paymentError);
          // Don't fail registration if payment/invoice creation fails
        }
      } else {
        console.log('💰 [PatientRegistration] No payment record created - totalAmount:', totalAmount, 'initialPayment:', initialPayment);
      }
      
      // Update room bed occupancy if room and bed are assigned
      if (registrationForm.roomNumber && registrationForm.bedNumber) {
        try {
          // Updating bed assignment in room
          
          // Find the room in Firebase
          const roomsRef = collection(db, 'rooms');
          const roomsSnapshot = await getDocs(roomsRef);
          const roomDoc = roomsSnapshot.docs.find(doc => {
            const data = doc.data();
            return data.roomNumber === registrationForm.roomNumber || doc.id === registrationForm.roomNumber;
          });
          
          if (roomDoc) {
            const roomData = roomDoc.data();
            console.log('📋 Current room data:', roomData);
            
            // Handle both old and new bed structure formats
            let updatedBeds = [];
            let currentOccupancy = 0;
            
            // Check if using new structure with availableBeds array of objects
            if (roomData.availableBeds && Array.isArray(roomData.availableBeds)) {
              updatedBeds = roomData.availableBeds.map((bed: any) => {
                // Handle both object and string formats
                const bedNumber = typeof bed === 'string' ? bed : (bed.bedNumber || bed.number || bed.id);
                const isOccupied = typeof bed === 'object' ? bed.isOccupied : false;
                
                if (bedNumber === registrationForm.bedNumber) {
                  return { 
                    bedNumber: bedNumber,
                    isOccupied: true, 
                    patientId: docRef.id,
                    patientName: registrationForm.fullName,
                    occupiedAt: new Date().toISOString()
                  };
                }
                return typeof bed === 'object' ? bed : { bedNumber: bed, isOccupied: false };
              });
              
              currentOccupancy = updatedBeds.filter((bed: any) => bed.isOccupied).length;
            } else {
              // Fallback: Create new bed structure
              const totalBeds = roomData.capacity || roomData.totalBeds || 1;
              updatedBeds = [];
              for (let i = 1; i <= totalBeds; i++) {
                const bedNum = `${roomData.roomNumber}${String.fromCharCode(64 + i)}`;
                updatedBeds.push({
                  bedNumber: bedNum,
                  isOccupied: bedNum === registrationForm.bedNumber,
                  patientId: bedNum === registrationForm.bedNumber ? docRef.id : undefined,
                  patientName: bedNum === registrationForm.bedNumber ? registrationForm.fullName : undefined,
                  occupiedAt: bedNum === registrationForm.bedNumber ? new Date().toISOString() : undefined
                });
              }
              currentOccupancy = 1;
            }
            
            // Calculate room status based on occupancy
            // Use the actual bed array length if capacity is not set properly
            const totalBeds = roomData.capacity || roomData.totalBeds || updatedBeds.length || 1;
            
            // Bed calculation complete - Room status determined by occupancy
            
            let roomStatus = 'Available';
            let statusColor = '#10B981';
            
            if (currentOccupancy === totalBeds) {
              // All beds are occupied
              roomStatus = 'Occupied';
              statusColor = '#EF4444';
              // All beds in room are now occupied
            } else if (currentOccupancy > 0 && currentOccupancy < totalBeds) {
              // Some beds are occupied but not all
              roomStatus = 'Partially Occupied';
              statusColor = '#F59E0B';
              console.log('  ⚠️ Status: PARTIALLY OCCUPIED');
            } else {
              // No beds occupied
              console.log('  🟢 Status: AVAILABLE');
            }
            // else: currentOccupancy === 0, status remains 'Available'
            
            // Update Firebase with new bed status
            await updateDoc(doc(db, 'rooms', roomDoc.id), {
              availableBeds: updatedBeds,
              currentOccupancy: currentOccupancy,
              status: roomStatus,
              statusColor: statusColor,
              occupancy: {
                current: currentOccupancy,
                total: totalBeds
              },
              updatedAt: Timestamp.now()
            });
            
            // Room bed occupancy updated successfully
          } else {
            console.warn('⚠️ Room not found in Firebase:', registrationForm.roomNumber);
          }
        } catch (error) {
          console.error('❌ Error updating room bed occupancy:', error);
          // Don't fail the registration if room update fails
        }
      }
      
      setSnackbar({ 
        open: true, 
        message: `✅ Registration Complete! Patient ${registrationForm.fullName} registered successfully with payment confirmation!`, 
          severity: 'success' 
        });
        setShowRegistrationModal(false);
        
        // Reset form and payment state
        setRegistrationForm({
          patientType: 'OP',
          fullName: '',
          age: '',
          gender: '',
          bloodGroup: '',
          phoneNumber: '',
          emergencyContact: '',
          address: '',
          doctor: '',
          department: '',
          roomNumber: '',
          bedNumber: '',
          referredBy: '',
          totalAmount: '',
          initialPayment: '',
          paymentMethod: 'Cash',
          paymentType: 'Registration Fee',
          symptoms: '',
          allergies: '',
          transactionId: '',
          razorpayPaymentId: '',
          razorpayOrderId: '',
          razorpaySignature: '',
          editTotalAmount: '',
          editPaidAmount: '',
          editDueAmount: '',
          billChangeReason: ''
        });
        
        setProcessingPayment(false);
        setRegistrationCompleting(false);
        // Don't reset payment completed immediately to prevent UI confusion
        // Reset it after a delay to allow UI to show success state
        setTimeout(() => {
          setPaymentCompleted(false);
        }, 2000);
        setRazorpayOrderId('');

        // Refresh the patient list and room data
        await handleRefresh();
        
        // Force reload rooms with a small delay to ensure Firebase propagation
        setTimeout(async () => {
          // Reloading rooms after registration
          await loadAvailableRooms();
        }, 1000);
    } catch (error: any) {
      console.error('❌ Error registering patient:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      let errorMessage = 'Registration failed. ';
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        errorMessage += 'Authentication required. Please logout and login again.';
      } else if (error.code === 'unavailable') {
        errorMessage += 'Firebase service unavailable. Please check your internet connection.';
      } else if (error.code === 'unauthenticated') {
        errorMessage += 'Please logout and login again to continue.';
      } else if (error.code === 'failed-precondition') {
        errorMessage += 'Database rules error. Please contact administrator.';
      } else if (error.message && error.message.includes('auth')) {
        errorMessage += 'Authentication error. Please refresh page and login again.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again. If problem persists, contact support.';
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
      setRegistrationCompleting(false);
    }
  };
  
  // Handle Razorpay payment for patient registration
  const handlePatientRazorpayPayment = async () => {
    console.log('💳 Initiating Razorpay payment for consultation...');
    console.log('🔍 Current State - processingPayment:', processingPayment, 'paymentCompleted:', paymentCompleted);
    
    // CRITICAL: Prevent duplicate payments - multiple safety checks
    if (processingPayment) {
      console.log('⚠️ Payment already in progress, preventing duplicate');
      setSnackbar({
        open: true,
        message: 'Payment is already being processed. Please wait...',
        severity: 'warning'
      });
      return;
    }
    
    if (paymentCompleted) {
      console.log('✅ Payment already completed, proceeding with registration instead of payment');
      setSnackbar({
        open: true,
        message: 'Payment already completed. Completing registration...',
        severity: 'info'
      });
      handlePatientRegistration();
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      // Validate payment amount
      const totalAmount = parseFloat(registrationForm.totalAmount) || 0;
      const initialPayment = parseFloat(registrationForm.initialPayment) || totalAmount;
      
      if (initialPayment <= 0) {
        setSnackbar({
          open: true,
          message: 'Payment amount must be greater than 0',
          severity: 'error'
        });
        setProcessingPayment(false);
        return;
      }
      
      // Prepare payment data for Razorpay
      const paymentData = {
        amount: initialPayment,
        patientId: `temp_${Date.now()}`, // Temporary ID since patient not created yet
        patientName: registrationForm.fullName,
        patientEmail: '', // Can be added to form later
        patientPhone: registrationForm.phoneNumber,
        paymentType: registrationForm.paymentType,
        description: `${registrationForm.paymentType} - ${registrationForm.fullName} (${registrationForm.patientType})`
      };
      
      console.log('💰 Payment data prepared:', paymentData);
      
      // Check Razorpay availability first
      const availability = RazorpayService.testRazorpayAvailability();
      if (!availability.available) {
        setSnackbar({
          open: true,
          message: `Razorpay Error: ${availability.error}`,
          severity: 'error'
        });
        setProcessingPayment(false);
        return;
      }
      
      // Initiate Razorpay payment
      await RazorpayService.initiatePayment(
        paymentData,
        // Success callback
        async (response: RazorpayResponse) => {
          console.log('✅ Razorpay payment successful:', response);
          
          try {
            // Verify payment data
            if (!response.razorpay_payment_id) {
              throw new Error('Invalid payment response - missing payment ID');
            }
            
            // CRITICAL: Mark payment as completed FIRST to prevent duplicates
            setPaymentCompleted(true);
            setProcessingPayment(false);
            
            // Store payment details in form
            setRazorpayOrderId(response.razorpay_order_id || '');
            setRegistrationForm(prev => ({
              ...prev,
              transactionId: response.razorpay_payment_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id || '',
              razorpaySignature: response.razorpay_signature || ''
            }));
            
            console.log('💾 Payment details stored successfully');
            console.log('🔒 Payment marked as completed - will NOT trigger again');
            
            setSnackbar({
              open: true,
              message: `✅ Payment Confirmed! ₹${initialPayment.toLocaleString()} - Click Complete Registration button`,
              severity: 'success'
            });
            
            // DO NOT auto-proceed to registration - let user click the button
            // This prevents race conditions and gives user control
            console.log('👆 User must click "Complete Registration" button to proceed');
            
          } catch (confirmError: any) {
            console.error('❌ Error confirming payment:', confirmError);
            setProcessingPayment(false);
            setSnackbar({
              open: true,
              message: `Payment successful but confirmation failed: ${confirmError.message}`,
              severity: 'warning'
            });
          }
        },
        // Failure callback
        (error: RazorpayError) => {
          console.error('❌ Razorpay payment failed:', error);
          
          setProcessingPayment(false);
          setPaymentCompleted(false);
          
          let errorMessage = 'Payment failed';
          
          if (error.code === 'BAD_REQUEST_ERROR') {
            errorMessage = 'Payment was cancelled by user';
          } else if (error.description) {
            errorMessage = error.description;
          } else if (error.reason) {
            errorMessage = error.reason;
          } else {
            errorMessage = 'Payment process was interrupted';
          }
          
          setSnackbar({
            open: true,
            message: `❌ ${errorMessage}. Please try again or use a different payment method.`,
            severity: 'error'
          });
          
          console.log('🔄 Payment failed, user can retry or use different method');
        }
      );
      
    } catch (error: any) {
      console.error('❌ Payment initiation error:', error);
      setProcessingPayment(false);
      setPaymentCompleted(false);
      
      setSnackbar({
        open: true,
        message: `Payment failed: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };
  
  // Handle cash payment confirmation
  const handleCashPayment = () => {
    const initialPayment = parseFloat(registrationForm.initialPayment) || 0;
    
    if (initialPayment > 0) {
      setSnackbar({
        open: true,
        message: `Cash payment of ₹${initialPayment.toLocaleString()} recorded`,
        severity: 'success'
      });
    }
    
    setPaymentCompleted(true);
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      console.log('🔄 Refreshing patient data from Firebase...');
      
      // Reset all filters to show all data
      setSearchQuery('');
      setSelectedFilter('All');
      setFromDate(null);
      setToDate(null);
      
      // The real-time listener will automatically update the patients
      // But we can manually fetch to force an immediate update
      const patientsRef = collection(db, 'patients');
      const patientsSnapshot = await getDocs(patientsRef);
      const firebasePatients = patientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (firebasePatients && firebasePatients.length > 0) {
        console.log('✅ Refreshed patients from Firebase:', firebasePatients.length);
        const transformedPatients = firebasePatients.map(transformPatientData);
        setPatients(transformedPatients);
        setSnackbar({ 
          open: true, 
          message: `Successfully loaded ${firebasePatients.length} patients`, 
          severity: 'success' 
        });
      } else {
        console.log('ℹ️ No patients found in Firebase');
        setPatients([]);
        setSnackbar({ 
          open: true, 
          message: 'No patients found in database', 
          severity: 'info' 
        });
      }
      
      console.log('✅ Patient data refreshed');
    } catch (error) {
      console.error('❌ Error refreshing patient data:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to refresh patient data. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setRefreshing(false);
    }
  }, []);

  const handleSyncRoomStatuses = async () => {
    setRefreshing(true);
    try {
      console.log('🔄 Syncing room statuses with patient data...');
      setSnackbar({ 
        open: true, 
        message: 'Synchronizing room statuses...', 
        severity: 'info' 
      });
      
      const result = await RoomManagementService.syncRoomStatuses();
      
      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: `${result.message}. ${result.updatedRooms === 0 ? 'All rooms are already in sync!' : ''}`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: result.message, 
          severity: 'error' 
        });
      }
    } catch (error: any) {
      console.error('❌ Error syncing room statuses:', error);
      setSnackbar({ 
        open: true, 
        message: `Failed to sync room statuses: ${error.message}`, 
        severity: 'error' 
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleCall = (patient: Patient) => {
    const phoneNumber = patient.phone.replace(/\s/g, '');
    setSnackbar({ 
      open: true, 
      message: `Calling ${patient.name} at ${patient.phone}...`, 
      severity: 'info' 
    });
    // Open phone dialer
    window.open(`tel:${phoneNumber}`, '_self');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Payment status colors (from React Native)
  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'paid':
      case 'fully paid':
      case 'success':
      case 'completed':
        return '#10B981'; // Green for paid
      case 'pending':
        return '#EF4444'; // Red for pending
      case 'partial':
      case 'partially paid':
        return '#F59E0B'; // Orange for partial
      case 'failed':
        return '#EF4444'; // Red for failed
      case 'refunded':
        return '#6B7280'; // Gray for refunded
      default:
        return '#6B7280'; // Gray for unknown
    }
  };

  // Payment type icons (from React Native)
  const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'consultation': return <MedicalIcon />;
      case 'tests': return <MedicalIcon />;
      case 'admission': return <BedIcon />;
      case 'surgery': return <MedicalIcon />;
      case 'medicine': return <MedicalIcon />;
      default: return <ReceiptIcon />;
    }
  };

  const handleEditPatient = async (patient: Patient) => {
    setEditingPatient(patient);
    
    // Calculate actual paid amount from payment transactions FIRST
    console.log('🔍 Calculating paid amount for patient:', patient.id);
    console.log('📊 Total patient payments loaded:', patientPayments.length);
    
    const patientPaymentRecords = patientPayments.filter(p => 
      p.source === 'payments' && 
      p.patientId === patient.id && 
      !p.id.includes('_tx_')
    );
    
    console.log('💰 Payment records for this patient:', patientPaymentRecords.length, patientPaymentRecords);
    
    // Sum all payment amounts (excluding failed/refunded)
    const actualPaidAmount = patientPaymentRecords
      .filter(p => p.status !== 'failed' && p.status !== 'refunded')
      .reduce((sum, payment) => sum + (payment.paidAmount || payment.amount || 0), 0);
    
    console.log('✅ Calculated actual paid amount:', actualPaidAmount);
    
    // Calculate correct due amount
    const currentTotal = patient.totalAmount || 0;
    const correctDueAmount = Math.max(0, currentTotal - actualPaidAmount);
    
    console.log('📈 Total:', currentTotal, 'Paid:', actualPaidAmount, 'Due:', correctDueAmount);
    
    // Pre-populate form with patient data AND calculated amounts
    setRegistrationForm({
      patientType: patient.patientType,
      fullName: patient.name,
      age: patient.age.toString(),
      gender: patient.gender,
      bloodGroup: patient.bloodGroup || '',
      phoneNumber: patient.phone,
      emergencyContact: patient.emergencyContact || '',
      address: patient.address || '',
      doctor: patient.doctor,
      department: patient.department || '',
      roomNumber: patient.room || '',
      bedNumber: patient.bedNo || '',
      referredBy: '',
      totalAmount: '',
      initialPayment: '',
      paymentMethod: 'Cash',
      paymentType: 'Registration Fee',
      symptoms: '',
      allergies: Array.isArray(patient.allergies) ? patient.allergies.join(', ') : (patient.allergies || ''),
      transactionId: '',
      razorpayPaymentId: '',
      razorpayOrderId: '',
      razorpaySignature: '',
      // Billing fields for edit - use calculated amounts
      editTotalAmount: currentTotal.toString(),
      editPaidAmount: actualPaidAmount.toString(),
      editDueAmount: correctDueAmount.toString(),
      billChangeReason: ''
    });
    
    // Refresh room data before opening modal
    await loadAvailableRooms();
    await loadAvailableDoctors();
    
    setShowEditModal(true);
    setSnackbar({ 
      open: true, 
      message: `Opening edit form for ${patient.name}...`, 
      severity: 'info' 
    });
  };

  const handleDeletePatient = async (patient: Patient) => {
    if (window.confirm(`Are you sure you want to permanently delete patient ${patient.name}? This action cannot be undone and will remove all patient data including their login credentials.`)) {
      try {
        console.log('🗑️ Deleting patient from Firebase:', patient.id);
        setIsLoading(true);
        
        // Step 1: Release the bed if patient has a room assignment
        if (patient.room && patient.bedNo) {
          console.log(`🛏️ Releasing bed ${patient.bedNo} in room ${patient.room} before deletion...`);
          
          const result = await RoomManagementService.updateRoomAvailabilityForPatient(
            patient.id,
            patient.room,
            patient.bedNo
          );
          
          if (result.success) {
            console.log(`✅ ${result.message}`);
          } else {
            console.warn(`⚠️ ${result.message}`);
          }
        }
        
        // Step 2: Delete patient from Firebase Authentication (if they have a user account)
        try {
          console.log('🔐 Attempting to delete user authentication account...');
          const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
          const authResult = await deleteUserAccount({ uid: patient.id });
          console.log('✅ User authentication deleted:', authResult.data);
        } catch (authError: any) {
          console.warn('⚠️ Auth deletion warning:', authError.message);
          // Continue with Firestore deletion even if auth deletion fails
        }
        
        // Step 3: Delete patient from Firestore
        const patientRef = doc(db, 'patients', patient.id);
        await deleteDoc(patientRef);
        
        // Refresh data
        await handleRefresh();
        await loadAvailableRooms();
        
        setSnackbar({ 
          open: true, 
          message: `Patient ${patient.name} has been permanently deleted including their login credentials${patient.room && patient.bedNo ? `. Bed ${patient.bedNo} in Room ${patient.room} is now available.` : ''}`, 
          severity: 'success' 
        });
        console.log('✅ Patient deleted successfully from web dashboard');
      } catch (error: any) {
        console.error('❌ Error deleting patient:', error);
        setSnackbar({ 
          open: true, 
          message: `Failed to delete patient: ${error.message || 'Unknown error'}`, 
          severity: 'error' 
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle patient discharge - navigate to discharge page
  const handleDischargePatient = (patient: Patient) => {
    // Navigate to discharge page with patient data
    navigate('/discharge', { state: { patient: patient } });
    setSnackbar({ 
      open: true, 
      message: `Navigating to discharge page for ${patient.name}...`, 
      severity: 'info' 
    });
  };

  // Handle change room
  const handleChangeRoom = (patient: Patient) => {
    setPatientForRoomChange(patient);
    setNewRoomNumber(patient.room || '');
    setNewBedNumber(patient.bedNo || '');
    setShowChangeRoomModal(true);
  };

  const handleConfirmRoomChange = async () => {
    if (!patientForRoomChange || !newRoomNumber || !newBedNumber) {
      setSnackbar({ 
        open: true, 
        message: 'Please select both room and bed', 
        severity: 'warning' 
      });
      return;
    }

    try {
      setIsLoading(true);
      // Changing room assignment for patient

      // Step 1: Release old bed if exists
      if (patientForRoomChange.room && patientForRoomChange.bedNo) {
        // Releasing old bed assignment
        
        const roomsRef = collection(db, 'rooms');
        const roomsSnapshot = await getDocs(roomsRef);
        const oldRoomDoc = roomsSnapshot.docs.find(doc => {
          const data = doc.data();
          return data.roomNumber === patientForRoomChange.room || doc.id === patientForRoomChange.room;
        });

        if (oldRoomDoc) {
          const roomData = oldRoomDoc.data();
          let updatedBeds = [];
          
          if (roomData.availableBeds && Array.isArray(roomData.availableBeds)) {
            updatedBeds = roomData.availableBeds.map((bed: any) => {
              const bedNumber = typeof bed === 'string' ? bed : (bed.bedNumber || bed.number || bed.id);
              
              if (bedNumber === patientForRoomChange.bedNo) {
                return {
                  bedNumber: bedNumber,
                  isOccupied: false
                };
              }
              return typeof bed === 'object' ? bed : { bedNumber: bed, isOccupied: false };
            });

            const currentOccupancy = updatedBeds.filter((bed: any) => bed.isOccupied).length;
            const totalBeds = roomData.capacity || roomData.totalBeds || updatedBeds.length || 1;
            
            let roomStatus = 'Available';
            let statusColor = '#10B981';
            if (currentOccupancy === totalBeds) {
              roomStatus = 'Occupied';
              statusColor = '#EF4444';
            } else if (currentOccupancy > 0) {
              roomStatus = 'Partially Occupied';
              statusColor = '#F59E0B';
            }

            await updateDoc(doc(db, 'rooms', oldRoomDoc.id), {
              availableBeds: updatedBeds,
              currentOccupancy: currentOccupancy,
              status: roomStatus,
              statusColor: statusColor,
              occupancy: {
                current: currentOccupancy,
                total: totalBeds
              },
              updatedAt: Timestamp.now()
            });
          }
        }
      }

      // Step 2: Assign new bed
      // Assigning new bed
      
      const roomsRef = collection(db, 'rooms');
      const roomsSnapshot = await getDocs(roomsRef);
      const newRoomDoc = roomsSnapshot.docs.find(doc => {
        const data = doc.data();
        return data.roomNumber === newRoomNumber || doc.id === newRoomNumber;
      });

      if (newRoomDoc) {
        const roomData = newRoomDoc.data();
        let updatedBeds = [];
        
        if (roomData.availableBeds && Array.isArray(roomData.availableBeds)) {
          updatedBeds = roomData.availableBeds.map((bed: any) => {
            const bedNumber = typeof bed === 'string' ? bed : (bed.bedNumber || bed.number || bed.id);
            
            if (bedNumber === newBedNumber) {
              return {
                bedNumber: bedNumber,
                isOccupied: true,
                patientId: patientForRoomChange.id,
                patientName: patientForRoomChange.name,
                occupiedAt: new Date().toISOString()
              };
            }
            return typeof bed === 'object' ? bed : { bedNumber: bed, isOccupied: false };
          });

          const currentOccupancy = updatedBeds.filter((bed: any) => bed.isOccupied).length;
          const totalBeds = roomData.capacity || roomData.totalBeds || updatedBeds.length || 1;
          
          let roomStatus = 'Available';
          let statusColor = '#10B981';
          if (currentOccupancy === totalBeds) {
            roomStatus = 'Occupied';
            statusColor = '#EF4444';
          } else if (currentOccupancy > 0) {
            roomStatus = 'Partially Occupied';
            statusColor = '#F59E0B';
          }

          await updateDoc(doc(db, 'rooms', newRoomDoc.id), {
            availableBeds: updatedBeds,
            currentOccupancy: currentOccupancy,
            status: roomStatus,
            statusColor: statusColor,
            occupancy: {
              current: currentOccupancy,
              total: totalBeds
            },
            updatedAt: Timestamp.now()
          });
        }
      }

      // Step 3: Update patient record
      const patientRef = doc(db, 'patients', patientForRoomChange.id);
      await updateDoc(patientRef, {
        room: newRoomNumber,
        roomNumber: newRoomNumber,
        bedNo: newBedNumber,
        bedNumber: newBedNumber,
        updatedAt: Timestamp.now()
      });

      setShowChangeRoomModal(false);
      setPatientForRoomChange(null);
      await handleRefresh();
      await loadAvailableRooms();

      setSnackbar({ 
        open: true, 
        message: `Patient ${patientForRoomChange.name} moved to Room ${newRoomNumber}, Bed ${newBedNumber}`, 
        severity: 'success' 
      });
      console.log('✅ Room change completed successfully');
    } catch (error: any) {
      console.error('❌ Error changing room:', error);
      setSnackbar({ 
        open: true, 
        message: `Failed to change room: ${error.message}`, 
        severity: 'error' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Razorpay payment
  const handleRazorpayPayment = async () => {
    console.log('🎯 handleRazorpayPayment called');
    
    // First, test Razorpay availability
    console.log('🔍 Testing Razorpay availability...');
    const availability = RazorpayService.testRazorpayAvailability();
    console.log('📋 Razorpay availability test result:', availability);
    
    if (!availability.available) {
      console.error('❌ Razorpay not available:', availability.error);
      setSavingPayment(false);
      setSnackbar({
        open: true,
        message: `Razorpay Error: ${availability.error}`,
        severity: 'error'
      });
      return;
    }
    
    if (!selectedPatient) {
      console.error('❌ No patient selected');
      return;
    }

    try {
      const totalAmount = paymentForm.items.reduce((sum, item) => sum + item.amount, 0);
      console.log('💰 Total amount:', totalAmount);

      if (totalAmount <= 0) {
        console.warn('⚠️ Invalid amount:', totalAmount);
        setSnackbar({
          open: true,
          message: 'Please add valid payment items with amount greater than 0',
          severity: 'warning'
        });
        return;
      }

      console.log('✅ Setting saving payment to true');
      setSavingPayment(true);

      console.log('💰 Creating Razorpay payment for amount:', totalAmount);

      // ✅ NEW LOGIC: Simple payment data structure (NO cumulative calculations)
      // Prepare payment data
      const paymentData = {
        amount: totalAmount,
        patientId: selectedPatient.id,
        patientName: selectedPatient.name,
        patientEmail: selectedPatient.email,
        patientPhone: selectedPatient.phone,
        paymentType: paymentForm.paymentType,
        description: paymentForm.description || `Payment for ${paymentForm.paymentType}`,
        items: paymentForm.items.filter(item => item.name && item.amount > 0)
      };

      console.log('💳 Initiating Razorpay payment...', paymentData);

      // Initiate Razorpay payment
      RazorpayService.initiatePayment(
        paymentData,
        // Success callback
        async (response: RazorpayResponse) => {
          console.log('✅ Razorpay payment successful:', response);

          try {
            // ✅ NEW LOGIC: Create payment record compatible with RazorpayService
            // Note: Still includes old fields for backward compatibility with RazorpayService
            const paymentRecord = {
              patientId: selectedPatient.id,
              patientName: selectedPatient.name,
              
              // THIS payment only (NOT cumulative)
              amount: totalAmount,
              
              // Date/Time (old format for compatibility)
              date: new Date().toISOString().split('T')[0],
              time: new Date().toLocaleTimeString(),
              
              // Payment method and transaction details
              paymentMethod: 'razorpay',
              transactionId: response.razorpay_payment_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              
              // Payment purpose/type
              type: RazorpayService.getTransactionType(paymentForm.paymentType),
              description: paymentData.description,
              
              // Status
              status: 'paid' as const,
              
              // Line items
              items: paymentData.items,
              
              // Temporary fields for backward compatibility (will be ignored)
              totalAmount: totalAmount,
              paidAmount: totalAmount,
              dueAmount: 0,
              
              // Metadata
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            };

            // Store in Firebase
            const result = await RazorpayService.storePaymentRecord(paymentRecord);

            if (result.success) {
              // ✅ Generate invoice for this payment
              console.log('📄 Generating invoice for Razorpay payment...');
              try {
                await generateInvoiceForPayment(result.paymentId || response.razorpay_payment_id, paymentRecord, db);
                console.log('✅ Invoice generated successfully');
              } catch (invoiceError) {
                console.error('⚠️ Payment saved but invoice generation failed:', invoiceError);
              }

              setSnackbar({
                open: true,
                message: `Payment of ₹${totalAmount.toLocaleString()} completed successfully via Razorpay!`,
                severity: 'success'
              });

              // Close modal and reset form
              setShowPaymentModal(false);
              setPaymentForm({
                amount: '',
                description: '',
                paymentMethod: 'Cash',
                paymentType: 'Consultation Fee',
                status: 'paid',
                transactionId: '',
                items: [{ name: '', description: '', quantity: 1, rate: 0, amount: 0 }],
                setTotalAmount: ''
              });
            } else {
              throw new Error(result.error || 'Failed to store payment record');
            }
          } catch (error: any) {
            console.error('❌ Error storing payment:', error);
            setSnackbar({
              open: true,
              message: `Payment succeeded but failed to store record: ${error.message}`,
              severity: 'warning'
            });
          } finally {
            setSavingPayment(false);
          }
        },
        // Failure callback
        (error: RazorpayError) => {
          console.error('❌ Razorpay payment failed:', error);
          console.error('Error details:', JSON.stringify(error, null, 2));
          console.error('Error code:', error.code);
          console.error('Error description:', error.description);
          console.error('Error source:', error.source);
          console.error('Error step:', error.step);
          console.error('Error reason:', error.reason);
          setSavingPayment(false);

          // Enhanced error message for debugging
          let errorMessage = 'Payment failed';
          if (error.code === 'PAYMENT_CANCELLED') {
            errorMessage = 'Payment was cancelled by user';
          } else if (error.description) {
            errorMessage = `Payment failed: ${error.description}`;
          } else if (error.reason) {
            errorMessage = `Payment failed: ${error.reason}`;
          } else if (error.code) {
            errorMessage = `Payment failed with code: ${error.code}`;
          } else {
            errorMessage = `Payment failed: ${JSON.stringify(error)}`;
          }

          // Only show error if it's not a user cancellation
          if (error.code !== 'PAYMENT_CANCELLED') {
            setSnackbar({
              open: true,
              message: errorMessage,
              severity: 'error'
            });
          } else {
            setSnackbar({
              open: true,
              message: 'Payment was cancelled',
              severity: 'info'
            });
          }
        }
      );
    } catch (error: any) {
      console.error('❌ Error initiating Razorpay payment:', error);
      console.error('Error stack:', error.stack);
      setSavingPayment(false);

      setSnackbar({
        open: true,
        message: `Failed to initiate payment: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Legacy discharge function (kept for backward compatibility)
  const handleDischargePatientDirectly = async (patient: Patient) => {
    if (!window.confirm(`Discharge patient ${patient.name}? This will free up the assigned bed.`)) {
      return;
    }

    try {
      console.log('🚪 Starting patient discharge process...');
      setIsLoading(true);

      // Step 1: Release the bed in the room
      if (patient.room && patient.bedNo) {
        // Releasing bed during discharge
        
        const roomsRef = collection(db, 'rooms');
        const roomsSnapshot = await getDocs(roomsRef);
        const roomDoc = roomsSnapshot.docs.find(doc => {
          const data = doc.data();
          return data.roomNumber === patient.room || doc.id === patient.room;
        });

        if (roomDoc) {
          const roomData = roomDoc.data();
          console.log('📋 Current room data:', roomData);

          // Update bed status to available
          let updatedBeds = [];
          let currentOccupancy = 0;

          if (roomData.availableBeds && Array.isArray(roomData.availableBeds)) {
            updatedBeds = roomData.availableBeds.map((bed: any) => {
              const bedNumber = typeof bed === 'string' ? bed : (bed.bedNumber || bed.number || bed.id);
              
              if (bedNumber === patient.bedNo) {
                // Release this bed - only include defined fields
                return {
                  bedNumber: bedNumber,
                  isOccupied: false
                };
              }
              return typeof bed === 'object' ? bed : { bedNumber: bed, isOccupied: false };
            });

            currentOccupancy = updatedBeds.filter((bed: any) => bed.isOccupied).length;
          }

          // Calculate room status
          const totalBeds = roomData.capacity || roomData.totalBeds || updatedBeds.length || 1;
          
          // Discharge - Bed calculation complete
          
          let roomStatus = 'Available';
          let statusColor = '#10B981';

          if (currentOccupancy === totalBeds) {
            // All beds are occupied
            roomStatus = 'Occupied';
            statusColor = '#EF4444';
            console.log('  ✅ Status: ALL BEDS OCCUPIED');
          } else if (currentOccupancy > 0 && currentOccupancy < totalBeds) {
            // Some beds are occupied but not all
            roomStatus = 'Partially Occupied';
            statusColor = '#F59E0B';
            console.log('  ⚠️ Status: PARTIALLY OCCUPIED');
          } else {
            // No beds occupied
            console.log('  🟢 Status: AVAILABLE');
          }
          // else: currentOccupancy === 0, status remains 'Available'

          // Update room in Firebase
          await updateDoc(doc(db, 'rooms', roomDoc.id), {
            availableBeds: updatedBeds,
            currentOccupancy: currentOccupancy,
            status: roomStatus,
            statusColor: statusColor,
            occupancy: {
              current: currentOccupancy,
              total: totalBeds
            },
            updatedAt: Timestamp.now()
          });

          console.log('✅ Bed released successfully');
          console.log(`📊 Updated room status: ${roomStatus} (${currentOccupancy}/${totalBeds} beds occupied)`);
        }
      }

      // Step 2: Update patient record (mark as discharged)
      const patientRef = doc(db, 'patients', patient.id);
      await updateDoc(patientRef, {
        status: 'Discharged',
        dischargeDate: new Date().toISOString(),
        room: null,
        roomNumber: null,
        bedNo: null,
        bedNumber: null,
        updatedAt: Timestamp.now()
      });

      console.log('✅ Patient record updated as discharged');

      // Refresh data
      await handleRefresh();
      await loadAvailableRooms();

      setSnackbar({
        open: true,
        message: `Patient ${patient.name} discharged successfully. Bed ${patient.bedNo} in Room ${patient.room} is now available.`,
        severity: 'success'
      });

      console.log('✅ Discharge process completed successfully');
    } catch (error: any) {
      console.error('❌ Error discharging patient:', error);
      setSnackbar({
        open: true,
        message: `Failed to discharge patient: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter menu handlers
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    console.log('Filter button clicked', event.currentTarget);
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filterValue: string) => {
    setSelectedFilter(filterValue);
    setFilterAnchorEl(null);
    
    // Add user feedback when filter is applied
    const filterMessages = {
      'All': 'Showing all patients',
      'IP': 'Filtering to show only In-Patients',
      'OP': 'Filtering to show only Out-Patients'
    };
    
    setSnackbar({ 
      open: true, 
      message: filterMessages[filterValue as keyof typeof filterMessages] || 'Filter applied', 
      severity: 'info' 
    });
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterAnchorEl) {
        const target = event.target as Element;
        const filterContainer = filterAnchorEl.closest('.filter-container');
        if (filterContainer && !filterContainer.contains(target)) {
          setFilterAnchorEl(null);
        }
      }
    };

    if (filterAnchorEl) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [filterAnchorEl]);

  // StatCard component (enhanced with gradients and icons)
  const StatCard: React.FC<{ title: string; count: number; color: string }> = ({ title, count, color }) => {
    const getGradient = (baseColor: string) => {
      switch (baseColor) {
        case '#FECACA': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        case '#BFDBFE': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'; 
        case '#BBF7D0': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
        default: return `linear-gradient(135deg, ${baseColor} 0%, ${baseColor}AA 100%)`;
      }
    };

    const getIcon = (title: string) => {
      switch (title) {
        case 'Total': return <PeopleIcon sx={{ fontSize: 40 }} />;
        case 'IP': return <BedIcon sx={{ fontSize: 40 }} />;
        case 'OP': return <OutPatientIcon sx={{ fontSize: 40 }} />;
        default: return <PeopleIcon sx={{ fontSize: 40 }} />;
      }
    };

    return (
      <Card sx={{ 
        background: getGradient(color),
        color: 'white',
        borderRadius: 3,
        minHeight: 140,
        boxShadow: `0 8px 32px ${color}40`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 40px ${color}60`,
        }
      }}>
        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            borderRadius: '50%', 
            p: 1.5,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {getIcon(title)}
          </Box>
          <Typography variant="h2" sx={{ fontWeight: 'bold', fontSize: '42px', mb: 0.5, textAlign: 'center' }}>
            {count}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', opacity: 0.9, textAlign: 'center' }}>
            {title} Patients
          </Typography>
        </CardContent>
      </Card>
    );
  };

  // PatientCard component (matching React Native PatientCard)
  const PatientCard: React.FC<{ patient: Patient }> = ({ patient }) => (
    <Card sx={{ 
      mb: 2, 
      borderRadius: 3, 
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        transform: 'translateY(-2px)'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        {/* Patient Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              bgcolor: '#007AFF', 
              width: 60, 
              height: 60,
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}>
              {patient.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                fontSize: '1.25rem',
                color: '#1F2937',
                mb: 0.5
              }}>
                {patient.name}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#6B7280', 
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                backgroundColor: '#F3F4F6',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                {patient.id}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
            <Chip 
              label={patient.patientType} 
              sx={{ 
                backgroundColor: patient.statusColor, 
                color: 'white', 
                fontWeight: 'bold',
                fontSize: '0.75rem',
                height: '28px'
              }}
            />
            <Chip 
              label={patient.statusText} 
              sx={{ 
                backgroundColor: `${patient.statusColor}20`, 
                color: patient.statusColor, 
                fontWeight: 'bold',
                fontSize: '0.75rem',
                height: '28px'
              }}
            />
          </Box>
        </Box>
        
        {/* Patient Details Grid */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          mb: 3,
          p: 2,
          backgroundColor: '#F8FAFC',
          borderRadius: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ fontSize: 18, color: '#4B5563' }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                Age & Gender
              </Typography>
              <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                {patient.age} years, {patient.gender}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 18, color: '#4B5563' }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                Phone
              </Typography>
              <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                {patient.phone}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HospitalIcon sx={{ fontSize: 18, color: '#4B5563' }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                Doctor & Department
              </Typography>
              <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                {patient.doctor}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                {patient.department}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MedicalIcon sx={{ fontSize: 18, color: '#4B5563' }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                Blood Group
              </Typography>
              <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                {patient.bloodGroup || 'Not specified'}
              </Typography>
            </Box>
          </Box>
          
          {patient.room && (
            <>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                p: 2,
                backgroundColor: '#EFF6FF',
                borderRadius: 2,
                border: '1px solid #DBEAFE',
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BedIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#3B82F6', fontSize: '0.75rem', fontWeight: 'bold' }}>
                      🏥 ROOM NUMBER
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 'bold', fontSize: '1rem' }}>
                      Room {patient.room}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BedIcon sx={{ fontSize: 18, color: '#059669' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.75rem', fontWeight: 'bold' }}>
                      🛏️ BED NUMBER
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 'bold', fontSize: '1rem' }}>
                      Bed {patient.bedNo}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: 18, color: '#4B5563' }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                    Admission Date
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                    {patient.admissionDate}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
          
          {patient.registrationDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon sx={{ fontSize: 18, color: '#4B5563' }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                  Registration Date
                </Typography>
                <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                  {patient.registrationDate}
                </Typography>
              </Box>
            </Box>
          )}

          {patient.emergencyContact && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ fontSize: 18, color: '#EF4444' }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                  Emergency Contact
                </Typography>
                <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                  {patient.emergencyContact}
                </Typography>
              </Box>
            </Box>
          )}

          {patient.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon sx={{ fontSize: 18, color: '#8B5CF6' }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                  Email Address
                </Typography>
                <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                  {patient.email}
                </Typography>
              </Box>
            </Box>
          )}

          {patient.address && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LocationIcon sx={{ fontSize: 18, color: '#F59E0B', mt: 0.25 }} />
              <Box>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                  Address
                </Typography>
                <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500', lineHeight: 1.4 }}>
                  {patient.address}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Medical Information Section (if available) */}
        {(patient.allergies?.length || patient.medicalHistory?.length || patient.currentMedications?.length) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ 
              fontSize: '1rem', 
              fontWeight: 'bold', 
              color: '#EF4444', 
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <MedicalIcon sx={{ fontSize: 20 }} />
              Medical Information
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr',
              gap: 2,
              p: 2,
              backgroundColor: '#FEF2F2',
              borderRadius: 2,
              border: '1px solid #FECACA'
            }}>
              {patient.allergies && patient.allergies.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#EF4444', fontSize: '0.75rem', fontWeight: 'bold' }}>
                    ⚠️ ALLERGIES
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                    {patient.allergies.join(', ')}
                  </Typography>
                </Box>
              )}
              
              {patient.medicalHistory && patient.medicalHistory.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#8B5CF6', fontSize: '0.75rem', fontWeight: 'bold' }}>
                    📋 MEDICAL HISTORY
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                    {patient.medicalHistory.join(', ')}
                  </Typography>
                </Box>
              )}
              
              {patient.currentMedications && patient.currentMedications.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.75rem', fontWeight: 'bold' }}>
                    💊 CURRENT MEDICATIONS
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: '500' }}>
                    {patient.currentMedications.join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Quick Actions Row */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5, 
          mb: 3, 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhoneIcon />}
            onClick={() => handleCall(patient)}
            sx={{ 
              color: '#22C55E', 
              borderColor: '#22C55E',
              '&:hover': {
                backgroundColor: '#22C55E',
                color: 'white'
              },
              fontWeight: '600',
              borderRadius: 2
            }}
          >
            Call
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReceiptIcon />}
            onClick={() => handleViewPaymentInvoices(patient)}
            sx={{ 
              color: '#3B82F6', 
              borderColor: '#3B82F6',
              '&:hover': {
                backgroundColor: '#3B82F6',
                color: 'white'
              },
              fontWeight: '600',
              borderRadius: 2
            }}
          >
            Payments
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReportsIcon />}
            onClick={() => handleViewMedicalReports(patient)}
            sx={{ 
              color: '#F59E0B', 
              borderColor: '#F59E0B',
              '&:hover': {
                backgroundColor: '#F59E0B',
                color: 'white'
              },
              fontWeight: '600',
              borderRadius: 2
            }}
          >
            Reports
          </Button>
          {/* Discharge button - Only show for IP patients or patients with assigned room/bed */}
          {(patient.patientType === 'IP' || patient.room) && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ChangeRoomIcon />}
                onClick={() => handleChangeRoom(patient)}
                sx={{ 
                  color: '#F59E0B', 
                  borderColor: '#F59E0B',
                  '&:hover': {
                    backgroundColor: '#F59E0B',
                    color: 'white'
                  },
                  fontWeight: '600',
                  borderRadius: 2
                }}
              >
                Change Room
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CheckIcon />}
                onClick={() => handleDischargePatient(patient)}
                sx={{ 
                  color: '#8B5CF6', 
                  borderColor: '#8B5CF6',
                  '&:hover': {
                    backgroundColor: '#8B5CF6',
                    color: 'white'
                  },
                  fontWeight: '600',
                  borderRadius: 2
                }}
              >
                Discharge
              </Button>
            </>
          )}
        </Box>

        {/* Card Actions */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pt: 2,
          borderTop: '1px solid #E5E7EB'
        }}>
          <Button
            variant="contained"
            startIcon={<ViewIcon />}
            onClick={() => handleViewDetails(patient)}
            sx={{ 
              backgroundColor: '#6366F1',
              '&:hover': { backgroundColor: '#4F46E5' },
              fontWeight: '600',
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            View Details
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton 
              onClick={() => handleEditPatient(patient)}
              sx={{ 
                color: '#3B82F6',
                '&:hover': { 
                  backgroundColor: '#3B82F610',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s'
              }}
              size="large"
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              onClick={() => handleDeletePatient(patient)}
              sx={{ 
                color: '#EF4444',
                '&:hover': { 
                  backgroundColor: '#EF444410',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s'
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
          Patient Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096' }}>
          Comprehensive patient management with payment tracking and medical reports
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <PeopleIcon sx={{ fontSize: 20 }} />
                <span>Patients List</span>
                <Badge badgeContent={stats.total} color="primary" sx={{ ml: 0.5 }} />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <PersonIcon sx={{ fontSize: 20 }} />
                <span>Patient Details</span>
              </Box>
            }
            disabled={!selectedPatient}
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <PaymentIcon sx={{ fontSize: 20 }} />
                <span>Payment Invoices</span>
              </Box>
            }
            disabled={!selectedPatient}
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <MedicalIcon sx={{ fontSize: 20 }} />
                <span>Medical Reports</span>
              </Box>
            }
            disabled={!selectedPatient}
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      
      {/* Patients List Tab */}
      <TabPanel value={currentTab} index={0}>
        {/* Stats Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: 3,
          mb: 4 
        }}>
          <StatCard title="Total" count={displayStats.total} color="#FECACA" />
          <StatCard title="IP" count={displayStats.inPatient} color="#BFDBFE" />
          <StatCard title="OP" count={displayStats.outPatient} color="#BBF7D0" />
        </Box>

        {/* Search and Filter */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          position: 'relative'
        }}>
          <TextField
            key="patient-search-input"
            fullWidth
            variant="outlined"
            placeholder="Search by name, ID, or phone"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            inputRef={searchInputRef}
            autoComplete="off"
            type="text"
            inputProps={{
              'aria-label': 'Search patients',
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#9CA3AF' }} />,
              disableUnderline: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#10B981',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#10B981',
                },
              },
              '& input': {
                padding: '10px 14px',
              }
            }}
          />
          
          {/* Date Range Filters */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 160 }
                },
                actionBar: {
                  actions: ['clear']
                }
              }}
            />
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 160 }
                },
                actionBar: {
                  actions: ['clear']
                }
              }}
              minDate={fromDate || undefined}
            />
          </LocalizationProvider>
          <Box sx={{ position: 'relative', display: 'inline-block' }} className="filter-container">
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={handleFilterClick}
              sx={{ 
                minWidth: selectedFilter !== 'All' ? 140 : 60,
                height: 48,
                fontSize: '0.875rem',
                borderColor: '#D1D5DB',
                color: '#374151',
                '&:hover': {
                  borderColor: '#6366F1',
                  color: '#6366F1',
                  backgroundColor: '#F0F9FF'
                },
                transition: 'all 0.2s ease',
                '& .MuiButton-startIcon': {
                  marginRight: selectedFilter !== 'All' ? 1 : 0
                }
              }}
            >
              {selectedFilter !== 'All' ? selectedFilter : ''}
            </Button>
            
            {/* Custom Dropdown - positioned relative to button */}
            {Boolean(filterAnchorEl) && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 0.5,
                  zIndex: 1300,
                  minWidth: 140,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  borderRadius: 2,
                  border: '1px solid #E5E7EB',
                  overflow: 'hidden',
                  backgroundColor: 'white'
                }}
                elevation={3}
              >
                <List sx={{ py: 0, m: 0 }}>
                  {filterOptions.map((option) => (
                    <ListItemButton 
                      key={option.value} 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Option clicked:', option.value);
                        handleFilterSelect(option.value);
                      }}
                      selected={selectedFilter === option.value}
                      sx={{
                        py: 2,
                        px: 2,
                        minHeight: 44,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#F3F4F6'
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#EBF8FF',
                          color: '#2563EB',
                          fontWeight: 'medium',
                          '&:hover': {
                            backgroundColor: '#DBEAFE'
                          }
                        }
                      }}
                    >
                      <ListItemText 
                        primary={option.label}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            fontWeight: selectedFilter === option.value ? 'medium' : 'normal'
                          }
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleRegisterNewPatient}
            sx={{ 
              backgroundColor: '#10B981',
              '&:hover': { backgroundColor: '#059669' },
              minWidth: 150
            }}
          >
            Add Patient
          </Button>
          <Tooltip title="Refresh patient list">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Loading and Patient List */}
        {refreshing && <LinearProgress sx={{ mb: 2 }} />}
        {loadingPatients && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: 200,
            gap: 2
          }}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="textSecondary">
              Loading patients from Firebase...
            </Typography>
          </Box>
        )}
        
        {/* Results Counter */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          px: 1
        }}>
          <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 'medium' }}>
            {filteredPatients.length === 0 
              ? 'No patients found' 
              : `Showing ${filteredPatients.length} of ${patients.length} patients`
            }
            {selectedFilter !== 'All' && (
              <Chip 
                label={`Filtered by: ${selectedFilter}`}
                size="small"
                sx={{ 
                  ml: 2, 
                  backgroundColor: '#EBF8FF', 
                  color: '#2563EB',
                  fontSize: '0.75rem'
                }}
                onDelete={() => handleFilterSelect('All')}
              />
            )}
          </Typography>
        </Box>
        
        {filteredPatients.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 1 }}>
              No patients found
            </Typography>
            <Typography variant="body2" sx={{ color: '#D1D5DB' }}>
              {searchQuery ? 'Try adjusting your search terms' : 'Register your first patient to get started'}
            </Typography>
          </Card>
        ) : (
          <Box>
            {filteredPatients.map((patient) => (
              <PatientCard key={patient.id} patient={patient} />
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Patient Details Tab */}
      <TabPanel value={currentTab} index={1}>
        {selectedPatient ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon />
                  Personal Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Full Name</Typography>
                    <Typography variant="body1">{selectedPatient.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Patient ID</Typography>
                    <Typography variant="body1">{selectedPatient.id}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Age & Gender</Typography>
                    <Typography variant="body1">{selectedPatient.age} years, {selectedPatient.gender}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Blood Group</Typography>
                    <Typography variant="body1">{selectedPatient.bloodGroup || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                    <Typography variant="body1">{selectedPatient.phone}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HospitalIcon />
                  Medical Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Patient Type</Typography>
                    <Chip 
                      label={selectedPatient.patientType} 
                      sx={{ backgroundColor: selectedPatient.statusColor, color: 'white' }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Attending Doctor</Typography>
                    <Typography variant="body1">{selectedPatient.doctor}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Department</Typography>
                    <Typography variant="body1">{selectedPatient.department}</Typography>
                  </Box>
                  {selectedPatient.room && (
                    <>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Room & Bed</Typography>
                        <Typography variant="body1">Room {selectedPatient.room}, Bed {selectedPatient.bedNo}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Admission Date</Typography>
                        <Typography variant="body1">{selectedPatient.admissionDate}</Typography>
                      </Box>
                    </>
                  )}
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Registration Date</Typography>
                    <Typography variant="body1">{selectedPatient.registrationDate}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#9CA3AF' }}>
              Select a patient to view details
            </Typography>
          </Card>
        )}
      </TabPanel>

      {/* Payment Invoices Tab */}
      <TabPanel value={currentTab} index={2}>
        {selectedPatient ? (
          <Box>
            {/* Patient Payment Header */}
            <Card sx={{ mb: 3, backgroundColor: '#F8FAFC' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6">Payment Invoices</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedPatient.name} • {selectedPatient.id}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowPaymentModal(true)}
                    sx={{ backgroundColor: '#10B981' }}
                  >
                    Add Payment
                  </Button>
                </Box>

                {/* Payment Summary Stats */}
                {loadingPayments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading payment data...</Typography>
                  </Box>
                ) : (() => {
                  // ✅ Calculate summary from PAYMENT RECORDS for accurate billing data
                  const summaryStats = (() => {
                    // Get all payment records (no more filtering of _tx_ since we removed those)
                    const paymentRecords = patientPayments.filter(p => p.source === 'payments');
                    
                    // Get the MAXIMUM totalAmount from all payment records
                    // This ensures we catch admin updates to the total (e.g., 500 -> 5000)
                    const maxTotalAmount = paymentRecords.reduce((max, payment) => {
                      const total = payment.totalAmount || 0;
                      return total > max ? total : max;
                    }, 0);
                    
                    // Use the max total, or fall back to selected patient's total
                    const currentTotalAmount = maxTotalAmount > 0 ? maxTotalAmount : (selectedPatient?.totalAmount || 0);
                    
                    // Calculate ACTUAL paid amount by summing all individual payment amounts
                    // Sum the 'amount' field (individual payment made in each transaction)
                    const actualPaidAmount = paymentRecords
                      .filter(p => p.status !== 'failed' && p.status !== 'refunded')
                      .reduce((sum, payment) => sum + (payment.amount || 0), 0);
                    
                    // Calculate correct due amount: Total - Paid
                    const correctDueAmount = Math.max(0, currentTotalAmount - actualPaidAmount);

                    console.log('📊 Payment Summary Calculation:', {
                      totalPayments: paymentRecords.length,
                      maxTotalAmount,
                      currentTotalAmount,
                      actualPaidAmount,
                      correctDueAmount,
                      payments: paymentRecords.map(p => ({ id: p.id, amount: p.amount, totalAmount: p.totalAmount }))
                    });

                    return {
                      totalTransactions: paymentRecords.length,
                      totalBilled: currentTotalAmount,    // Use max total amount from all payment records
                      totalPaid: actualPaidAmount,        // Sum of individual transaction amounts
                      dueAmount: correctDueAmount         // Calculate: Total - Paid
                    };
                  })();

                  return (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ color: '#1F2937', fontWeight: 'bold' }}>
                          {summaryStats.totalTransactions}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">Total Transactions</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ color: '#3B82F6', fontWeight: 'bold' }}>
                          ₹{summaryStats.totalBilled.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">Total Amount</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 'bold' }}>
                          ₹{summaryStats.totalPaid.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">Total Paid</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                        <Typography variant="h4" sx={{ color: summaryStats.dueAmount > 0 ? '#EF4444' : '#10B981', fontWeight: 'bold' }}>
                          ₹{summaryStats.dueAmount.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">Due Amount</Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Payment List */}
            {loadingPayments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={48} />
              </Box>
            ) : patientPayments.length === 0 ? (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <PaymentIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 1 }}>
                  No Payment Transactions Found
                </Typography>
                <Typography variant="body2" sx={{ color: '#D1D5DB' }}>
                  Payment transactions will appear here in real-time
                </Typography>
                <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 1, display: 'block' }}>
                  Connected to: payments collection
                </Typography>
              </Card>
            ) : (
              <Box>
                {patientPayments.map((payment) => {
                  // Auto-display paid status if patient's due amount is 0
                  const displayStatus = selectedPatient?.dueAmount === 0 ? 'paid' : payment.status;
                  
                  return (
                  <Card key={payment.id} sx={{ mb: 2 }}>
                    <CardContent>
                      {/* Payment Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                            backgroundColor: '#EBF4FF', 
                            p: 1, 
                            borderRadius: '50%', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {getTypeIcon(payment.type)}
                          </Box>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {payment.id}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              {payment.description}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {payment.date} • {payment.time}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            ₹{payment.amount.toLocaleString()}
                          </Typography>
                          <Chip 
                            label={displayStatus.toUpperCase()}
                            sx={{ 
                              backgroundColor: getStatusColor(displayStatus),
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Payment Method */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <MoneyIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                          <Typography variant="body2" color="textSecondary">
                            {payment.paymentMethod.charAt(0).toUpperCase() + payment.paymentMethod.slice(1)}
                          </Typography>
                          {/* Only show transaction ID for Razorpay payments */}
                          {payment.transactionId && payment.paymentMethod.toLowerCase() === 'razorpay' && (
                            <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                              TXN: {payment.transactionId}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Items Breakdown */}
                      {payment.items && payment.items.length > 0 && (
                        <Box sx={{ backgroundColor: '#F8FAFC', borderRadius: 2, p: 2, mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Items:
                          </Typography>
                          {payment.items.slice(0, 2).map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2">• {item.name}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: '500' }}>₹{item.amount}</Typography>
                            </Box>
                          ))}
                          {payment.items.length > 2 && (
                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                              +{payment.items.length - 2} more items
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Actions */}
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button 
                          size="small" 
                          startIcon={<ViewIcon />} 
                          variant="outlined"
                          onClick={() => handleViewInvoice(payment)}
                        >
                          View
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<ShareIcon />} 
                          variant="outlined"
                          onClick={() => handleShareInvoice(payment)}
                        >
                          Share
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<DownloadIcon />} 
                          variant="outlined"
                          onClick={() => handleDownloadInvoice(payment)}
                        >
                          Download
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        ) : (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <PaymentIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#9CA3AF' }}>
              Select a patient to view payment invoices
            </Typography>
          </Card>
        )}
      </TabPanel>

      {/* Medical Reports Tab */}
      <TabPanel value={currentTab} index={3}>
        {selectedPatient ? (
          <Box>
            <Card sx={{ mb: 3, backgroundColor: '#F8FAFC' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">Medical Reports</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedPatient.name} • {selectedPatient.id}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 16 }} /> {selectedPatient.phone}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ backgroundColor: '#10B981' }}
                  >
                    Add Report
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {loadingReports ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={48} />
                <Typography sx={{ ml: 2 }}>Loading medical reports...</Typography>
              </Box>
            ) : medicalReports.length === 0 ? (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <MedicalIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#9CA3AF', mb: 1 }}>
                  No Medical Reports Available
                </Typography>
                <Typography variant="body2" sx={{ color: '#D1D5DB' }}>
                  Medical reports and test results will appear here
                </Typography>
              </Card>
            ) : (
              <Box>
                {medicalReports.map((report) => (
                  <Card key={report.id} sx={{ mb: 2 }}>
                    <CardContent>
                      {/* Report Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                            backgroundColor: '#FEF3C7', 
                            p: 1, 
                            borderRadius: '50%', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <MedicalIcon sx={{ color: '#F59E0B' }} />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {report.reportType}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {report.date}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Dr. {report.doctor} • {report.department}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip 
                          label={report.status.toUpperCase()}
                          sx={{ 
                            backgroundColor: report.status === 'completed' ? '#10B981' : '#F59E0B',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>

                      {/* Report Details */}
                      {report.findings && (
                        <Box sx={{ mb: 2, p: 2, backgroundColor: '#F8FAFC', borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Findings:
                          </Typography>
                          <Typography variant="body2">{report.findings}</Typography>
                        </Box>
                      )}

                      {report.recommendations && (
                        <Box sx={{ mb: 2, p: 2, backgroundColor: '#FEF3C7', borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Recommendations:
                          </Typography>
                          <Typography variant="body2">{report.recommendations}</Typography>
                        </Box>
                      )}

                      {/* Attachments */}
                      {report.attachments && report.attachments.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Attachments ({report.attachments.length}):
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {report.attachments.map((attachment, index) => (
                              <Chip 
                                key={index}
                                label={`File ${index + 1}`}
                                icon={<DownloadIcon />}
                                onClick={() => window.open(attachment, '_blank')}
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Actions */}
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button 
                          size="small" 
                          startIcon={<ViewIcon />} 
                          variant="outlined"
                          onClick={() => handleViewReport(report)}
                        >
                          View
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<PrintIcon />} 
                          variant="outlined"
                          onClick={() => handlePrintReport(report)}
                        >
                          Print
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<ShareIcon />} 
                          variant="outlined"
                          onClick={() => handleShareReport(report)}
                        >
                          Share
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<DownloadIcon />} 
                          variant="outlined"
                          onClick={() => handleDownloadReport(report)}
                        >
                          Download
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <MedicalIcon sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#9CA3AF' }}>
              Select a patient to view medical reports
            </Typography>
          </Card>
        )}
      </TabPanel>

      {/* Patient Registration Modal */}
      <Dialog 
        open={showRegistrationModal} 
        onClose={handleCloseRegistrationModal}
        maxWidth="md"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '95vh',
            height: 'auto',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#F8FAFC', 
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AddIcon sx={{ color: '#10B981' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
              Register New Patient
            </Typography>
          </Box>
          <IconButton onClick={handleCloseRegistrationModal}>
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'auto',
          maxHeight: 'calc(95vh - 160px)',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a1a1a1',
          },
        }}>
          <Box sx={{ p: 4 }}>
            {/* Scroll Tip */}
            <Alert severity="info" sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">
                📝 Complete form below - {registrationForm.patientType === 'IP' ? 'IP patients require room & bed assignment' : 'OP form is simplified for consultations'}
              </Typography>
            </Alert>

            {/* Patient Type Section */}
            <Box sx={{ 
              mb: 4, 
              p: 3, 
              backgroundColor: '#F8FAFC', 
              borderRadius: 2,
              border: '1px solid #E5E7EB' 
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1F2937', fontWeight: 'bold' }}>
                🏥 Patient Type
              </Typography>
              <FormControl fullWidth required>
                <InputLabel>Patient Type *</InputLabel>
                <Select 
                  value={registrationForm.patientType}
                  label="Patient Type *"
                  onChange={(e) => setRegistrationForm({...registrationForm, patientType: e.target.value})}
                >
                  <MenuItem value="OP">
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>OP - Outpatient</Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>Consultations, checkups, minor procedures</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="IP">
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>IP - Inpatient</Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>Admitted patients requiring hospital stay</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Basic Information */}
            <Box sx={{ 
              mb: 4, 
              p: 3, 
              backgroundColor: '#FFFBEB', 
              borderRadius: 2,
              border: '1px solid #FCD34D' 
            }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#1F2937', fontWeight: 'bold' }}>
                👤 Basic Information
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr' },
                gap: 3,
                mb: 3
              }}>
                <TextField
                  fullWidth
                  required
                  label="Full Name *"
                  value={registrationForm.fullName}
                  onChange={(e) => setRegistrationForm({...registrationForm, fullName: e.target.value})}
                  placeholder="Enter patient's full name"
                />
                <TextField
                  fullWidth
                  required
                  label="Age *"
                  type="number"
                  value={registrationForm.age}
                  onChange={(e) => setRegistrationForm({...registrationForm, age: e.target.value})}
                  placeholder="Age"
                  inputProps={{ min: 0, max: 120 }}
                />
              </Box>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3
              }}>
                <FormControl fullWidth required>
                  <InputLabel>Gender *</InputLabel>
                  <Select 
                    value={registrationForm.gender}
                    label="Gender *"
                    onChange={(e) => setRegistrationForm({...registrationForm, gender: e.target.value})}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Blood Group"
                  value={registrationForm.bloodGroup}
                  onChange={(e) => setRegistrationForm({...registrationForm, bloodGroup: e.target.value})}
                  placeholder="e.g., O+, A+, B-, AB+"
                />
              </Box>
            </Box>

            {/* Contact Information */}
            <Box sx={{ 
              mb: 4, 
              p: 3, 
              backgroundColor: '#EFF6FF', 
              borderRadius: 2,
              border: '1px solid #93C5FD' 
            }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#1F2937', fontWeight: 'bold' }}>
                📞 Contact Information
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3,
                mb: 3
              }}>
                <TextField
                  fullWidth
                  required
                  label="Phone Number *"
                  value={registrationForm.phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    if (value.length <= 10) {
                      setRegistrationForm({...registrationForm, phoneNumber: value});
                    }
                  }}
                  placeholder="10 digit number"
                  inputProps={{ maxLength: 10 }}
                  helperText={registrationForm.phoneNumber && registrationForm.phoneNumber.length !== 10 ? "Must be exactly 10 digits" : "Enter exactly 10 digits"}
                  error={registrationForm.phoneNumber.length > 0 && registrationForm.phoneNumber.length !== 10}
                />
                <TextField
                  fullWidth
                  label={registrationForm.patientType === 'IP' ? 'Emergency Contact *' : 'Emergency Contact'}
                  value={registrationForm.emergencyContact}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    if (value.length <= 10) {
                      setRegistrationForm({...registrationForm, emergencyContact: value});
                    }
                  }}
                  placeholder="10 digit number"
                  inputProps={{ maxLength: 10 }}
                  helperText={registrationForm.emergencyContact && registrationForm.emergencyContact.length !== 10 && registrationForm.emergencyContact.length > 0 ? "Must be exactly 10 digits" : "Enter exactly 10 digits"}
                  error={registrationForm.emergencyContact.length > 0 && registrationForm.emergencyContact.length !== 10}
                  required={registrationForm.patientType === 'IP'}
                />
              </Box>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={registrationForm.address}
                onChange={(e) => setRegistrationForm({...registrationForm, address: e.target.value})}
                placeholder="Complete address"
              />
            </Box>

            {/* Medical Information */}
            <Box sx={{ 
              mb: 4, 
              p: 3, 
              backgroundColor: '#F0FDF4', 
              borderRadius: 2,
              border: '1px solid #86EFAC' 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#1F2937', fontWeight: 'bold' }}>
                  🩺 Medical Assignment
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadAvailableDoctors}
                  disabled={loadingDoctors}
                  sx={{ 
                    color: '#10B981',
                    borderColor: '#10B981',
                    '&:hover': {
                      backgroundColor: '#F0FDF4',
                      borderColor: '#059669'
                    }
                  }}
                  variant="outlined"
                >
                  {loadingDoctors ? 'Loading...' : 'Refresh Doctors'}
                </Button>
              </Box>
              {/* Debug Info */}
              <Typography variant="caption" sx={{ 
                display: 'block',
                color: '#6B7280', 
                mb: 2,
                backgroundColor: '#F8FAFC',
                p: 1,
                borderRadius: 1
              }}>
                📋 Status: {loadingDoctors ? 'Loading...' : `Loaded ${availableDoctors?.length || 0} doctors`} | 
                Backend: {availableDoctors?.length > 0 ? 'Connected ✅' : 'Using Fallback ⚠️'}
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3,
                mb: 3
              }}>
                <FormControl fullWidth required>
                  <InputLabel>Assigned Doctor *</InputLabel>
                  <Select 
                    value={registrationForm.doctor}
                    label="Assigned Doctor *"
                    onChange={(e) => {
                      const selectedDoctor = availableDoctors.find(doc => doc.name === e.target.value);
                      setRegistrationForm({
                        ...registrationForm, 
                        doctor: e.target.value,
                        department: selectedDoctor?.department || registrationForm.department
                      });
                    }}
                    disabled={loadingDoctors}
                  >
                    {loadingDoctors ? (
                      <MenuItem value="" disabled>Loading doctors...</MenuItem>
                    ) : (availableDoctors && availableDoctors.length > 0) ? (
                      availableDoctors
                        .filter(doctor => doctor.isActive !== false)
                        .map(doctor => (
                          <MenuItem key={doctor.id} value={doctor.name}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {doctor.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                {doctor.specialization} • {doctor.department}
                                {doctor.consultationFee && ` • ₹${doctor.consultationFee}`}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))
                    ) : (
                      // Only show static options if backend completely fails
                      <>
                        <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                          ⚠️ Backend doctors unavailable - Using fallback
                        </MenuItem>
                        <MenuItem value="Dr. Ramesh Kumar">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Dr. Ramesh Kumar</Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280' }}>General Medicine • ₹600</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="Dr. Priya Sharma">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Dr. Priya Sharma</Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280' }}>Cardiology • ₹900</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="Dr. Arjun Patel">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Dr. Arjun Patel</Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280' }}>Orthopedics • ₹750</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="Dr. Sita Reddy">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Dr. Sita Reddy</Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280' }}>Gynecology • ₹700</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="Dr. Kiran Rao">
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Dr. Kiran Rao</Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280' }}>Pediatrics • ₹650</Typography>
                          </Box>
                        </MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  required
                  label="Department *"
                  value={registrationForm.department}
                  onChange={(e) => setRegistrationForm({...registrationForm, department: e.target.value})}
                  placeholder="e.g., General Medicine, Cardiology"
                />
              </Box>
              
              {/* Symptoms - Different for OP vs IP */}
              <TextField
                fullWidth
                label={registrationForm.patientType === 'IP' ? 'Admission Reason & Symptoms' : 'Symptoms/Reason for Visit'}
                multiline
                rows={registrationForm.patientType === 'IP' ? 3 : 2}
                value={registrationForm.symptoms}
                onChange={(e) => setRegistrationForm({...registrationForm, symptoms: e.target.value})}
                placeholder={
                  registrationForm.patientType === 'IP' 
                    ? 'Reason for admission and current symptoms' 
                    : 'Chief complaint and symptoms'
                }
                sx={{ mb: 2 }}
              />
              
              {/* Allergies - More important for IP */}
              <TextField
                fullWidth
                label={registrationForm.patientType === 'IP' ? 'Allergies & Medical History *' : 'Known Allergies'}
                multiline
                rows={registrationForm.patientType === 'IP' ? 2 : 1}
                value={registrationForm.allergies}
                onChange={(e) => setRegistrationForm({...registrationForm, allergies: e.target.value})}
                placeholder={
                  registrationForm.patientType === 'IP' 
                    ? 'Drug allergies, medical conditions, previous surgeries' 
                    : 'Any known allergies'
                }
                required={registrationForm.patientType === 'IP'}
              />
            </Box>

            {/* Room & Bed Assignment - Different for IP and OP */}
            {registrationForm.patientType === 'IP' ? (
              // IP Patient - Full Room & Bed Assignment (Required)
              <Box sx={{ 
                mb: 4,
                p: 3,
                backgroundColor: '#EFF6FF',
                border: '2px solid #3B82F6',
                borderRadius: 3,
                position: 'relative'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: -1,
                  left: 16,
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: '0 0 8px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  IP PATIENT - ROOM & BED REQUIRED
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, mt: 2 }}>
                  <Typography variant="h6" sx={{ 
                    color: '#1F2937', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontWeight: 'bold'
                  }}>
                    <BedIcon sx={{ color: '#3B82F6' }} />
                    🏨 Hospital Room & Bed Assignment
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadAvailableRooms}
                    disabled={loadingRooms}
                    sx={{ 
                      color: '#3B82F6',
                      borderColor: '#3B82F6',
                      '&:hover': {
                        backgroundColor: '#EFF6FF',
                        borderColor: '#2563EB'
                      }
                    }}
                    variant="outlined"
                  >
                    {loadingRooms ? 'Loading...' : 'Refresh Rooms'}
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ color: '#374151', mb: 3, backgroundColor: '#F8FAFC', p: 2, borderRadius: 2 }}>
                  📋 <strong>Required for admission:</strong> Room and bed will be assigned for the patient's stay duration. 
                  Real-time available beds are fetched from the system.
                  {availableRooms.length > 0 && (
                    <Box component="span" sx={{ display: 'block', mt: 1, color: '#10B981', fontWeight: 'bold' }}>
                      ✅ {availableRooms.filter(r => r.availableBeds?.some(b => !b.isOccupied)).length} rooms with available beds loaded from database
                    </Box>
                  )}
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 3
                }}>
                  <FormControl fullWidth required>
                    <InputLabel>Hospital Room *</InputLabel>
                    <Select 
                      value={registrationForm.roomNumber}
                      label="Hospital Room *"
                      onChange={(e) => setRegistrationForm({...registrationForm, roomNumber: e.target.value, bedNumber: ''})}
                      disabled={loadingRooms}
                      onOpen={() => {
                        console.log('🏥 Room dropdown opened (IP)');
                        console.log('📊 Available rooms:', availableRooms);
                        console.log('🔢 Total rooms count:', availableRooms.length);
                        if (availableRooms.length > 0) {
                          console.log('🔍 Sample room:', availableRooms[0]);
                        }
                      }}
                    >
                      {loadingRooms ? (
                        <MenuItem value="" disabled>Loading rooms...</MenuItem>
                      ) : availableRooms.length > 0 ? (
                        (() => {
                          const filteredRooms = availableRooms.filter(room => {
                            const hasAvailableBeds = room.availableBeds?.some(bed => !bed.isOccupied);
                            const isAvailable = room.status === 'available';
                            return hasAvailableBeds && isAvailable;
                          });
                          
                          console.log(`✅ Showing ${filteredRooms.length} filtered rooms out of ${availableRooms.length} total`);
                          
                          if (filteredRooms.length === 0) {
                            // Show all rooms if filtering is too strict
                            console.warn('⚠️ No rooms passed filter, showing all rooms');
                            return availableRooms.map(room => (
                              <MenuItem key={room.id} value={room.roomNumber}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    🏥 Room {room.roomNumber} - {room.roomType?.toUpperCase() || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                    {room.availableBeds?.length || 0} beds • {room.department || 'General'} • Status: {room.status || 'unknown'}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ));
                          }
                          
                          return filteredRooms.map(room => (
                            <MenuItem key={room.id} value={room.roomNumber}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  🏥 Room {room.roomNumber} - {room.roomType?.toUpperCase()}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                  ✅ {room.availableBeds?.filter(bed => !bed.isOccupied).length}/{room.capacity} beds available • {room.department || 'General'}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ));
                        })()
                      ) : (
                        <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                          ⚠️ No rooms loaded. Click "Refresh Rooms" button above.
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required>
                    <InputLabel>Bed Number *</InputLabel>
                    <Select 
                      value={registrationForm.bedNumber}
                      label="Bed Number *"
                      onChange={(e) => {
                        console.log('✅ Bed selected (IP):', e.target.value);
                        setRegistrationForm({...registrationForm, bedNumber: e.target.value as string});
                      }}
                      disabled={!registrationForm.roomNumber || loadingRooms}
                      onOpen={() => {
                        console.log('🛏️ Bed dropdown opened (IP)');
                        console.log('📍 Selected room:', registrationForm.roomNumber);
                        const room = availableRooms.find(r => r.roomNumber === registrationForm.roomNumber);
                        console.log('🔍 Found room:', room);
                        if (room) {
                          console.log('📊 Room beds:', room.availableBeds);
                          const available = room.availableBeds?.filter(bed => {
                            const isOcc = typeof bed === 'object' ? (bed.isOccupied === true) : false;
                            return !isOcc;
                          });
                          console.log('✅ Available beds:', available);
                        }
                      }}
                    >
                      {!registrationForm.roomNumber ? (
                        <MenuItem value="" disabled>Select room first</MenuItem>
                      ) : loadingRooms ? (
                        <MenuItem value="" disabled>Loading beds...</MenuItem>
                      ) : (() => {
                        const selectedRoom = availableRooms.find(room => room.roomNumber === registrationForm.roomNumber);
                        
                        console.log('🔎 Looking for room:', registrationForm.roomNumber);
                        console.log('📋 Available rooms:', availableRooms.map(r => r.roomNumber));
                        console.log('✅ Selected room found:', selectedRoom);
                        
                        if (!selectedRoom) {
                          return (
                            <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                              ⚠️ Room not found: {registrationForm.roomNumber}
                            </MenuItem>
                          );
                        }
                        
                        if (!selectedRoom.availableBeds || selectedRoom.availableBeds.length === 0) {
                          return (
                            <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                              ⚠️ No beds configured for this room
                            </MenuItem>
                          );
                        }
                        
                        // Filter beds - check isOccupied property carefully (IP Section)
                        const availableBeds = selectedRoom.availableBeds.filter(bed => {
                          const isOccupied = typeof bed === 'object' ? (bed.isOccupied === true) : false;
                          const bedNum = typeof bed === 'object' ? bed.bedNumber : bed;
                          console.log(`🔍 IP Bed ${bedNum}: isOccupied=${isOccupied}, bed=`, bed);
                          return !isOccupied;
                        });
                        console.log(`🛏️ IP Available beds for room ${selectedRoom.roomNumber}:`, availableBeds);
                        console.log(`📊 IP Total: ${selectedRoom.availableBeds.length}, Available: ${availableBeds.length}`);
                        
                        if (availableBeds.length === 0) {
                          return (
                            <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                              ⚠️ All {selectedRoom.availableBeds.length} beds occupied in this room
                            </MenuItem>
                          );
                        }
                        
                        return availableBeds.map((bed, index) => {
                          // Handle different bed data structures
                          const bedData = bed as any;
                          const bedNumber = bed.bedNumber || bedData.number || bedData.id || `${index + 1}`;
                          console.log(`🔍 Processing bed:`, bed, `Bed Number: ${bedNumber}`);
                          
                          return (
                            <MenuItem 
                              key={bedNumber} 
                              value={bedNumber}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#F3F4F6' }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography>🛏️ Bed {bedNumber}</Typography>
                                <Box sx={{ 
                                  backgroundColor: '#10B981', 
                                  color: 'white', 
                                  px: 1, 
                                  borderRadius: 1, 
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}>
                                  AVAILABLE
                                </Box>
                              </Box>
                            </MenuItem>
                          );
                        });
                      })()}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ) : (
              // OP Patient - Full Room & Bed Assignment (Same as IP)
              <Box sx={{ 
                mb: 4,
                p: 3,
                backgroundColor: '#F0FDF4',
                border: '2px solid #10B981',
                borderRadius: 3,
                position: 'relative'
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: -1,
                  left: 16,
                  backgroundColor: '#10B981',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: '0 0 8px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  OP PATIENT - ROOM & BED ASSIGNMENT
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, mt: 2 }}>
                  <Typography variant="h6" sx={{ 
                    color: '#1F2937', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontWeight: 'bold'
                  }}>
                    <BedIcon sx={{ color: '#10B981' }} />
                    🏨 Hospital Room & Bed Assignment
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadAvailableRooms}
                    disabled={loadingRooms}
                    sx={{ 
                      color: '#10B981',
                      borderColor: '#10B981',
                      '&:hover': {
                        backgroundColor: '#F0FDF4',
                        borderColor: '#059669'
                      }
                    }}
                    variant="outlined"
                  >
                    {loadingRooms ? 'Loading...' : 'Refresh Rooms'}
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ color: '#374151', mb: 3, backgroundColor: '#F8FAFC', p: 2, borderRadius: 2 }}>
                  📋 <strong>Room assignment for OP patient:</strong> Room and bed will be assigned for consultation/treatment. 
                  Real-time available beds are fetched from the system.
                  {availableRooms.length > 0 && (
                    <Box component="span" sx={{ display: 'block', mt: 1, color: '#10B981', fontWeight: 'bold' }}>
                      ✅ {availableRooms.filter(r => r.availableBeds?.some(b => !b.isOccupied)).length} rooms with available beds loaded from database
                    </Box>
                  )}
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 3
                }}>
                  <FormControl fullWidth required>
                    <InputLabel>Hospital Room *</InputLabel>
                    <Select 
                      value={registrationForm.roomNumber}
                      label="Hospital Room *"
                      onChange={(e) => setRegistrationForm({...registrationForm, roomNumber: e.target.value, bedNumber: ''})}
                      disabled={loadingRooms}
                      onOpen={() => {
                        console.log('🏥 Room dropdown opened (OP)');
                        console.log('📊 Available rooms:', availableRooms);
                        console.log('🔢 Total rooms count:', availableRooms.length);
                      }}
                    >
                      {loadingRooms ? (
                        <MenuItem value="" disabled>Loading rooms...</MenuItem>
                      ) : availableRooms.length > 0 ? (
                        (() => {
                          const filteredRooms = availableRooms.filter(room => {
                            const hasAvailableBeds = room.availableBeds?.some(bed => !bed.isOccupied);
                            const isAvailable = room.status === 'available';
                            return hasAvailableBeds && isAvailable;
                          });
                          
                          console.log(`✅ Showing ${filteredRooms.length} filtered rooms (OP) out of ${availableRooms.length} total`);
                          
                          if (filteredRooms.length === 0) {
                            console.warn('⚠️ No rooms passed filter (OP), showing all rooms');
                            return availableRooms.map(room => (
                              <MenuItem key={room.id} value={room.roomNumber}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    🏥 Room {room.roomNumber} - {room.roomType?.toUpperCase() || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                    {room.availableBeds?.length || 0} beds • {room.department || 'General'} • Status: {room.status || 'unknown'}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ));
                          }
                          
                          return filteredRooms.map(room => (
                            <MenuItem key={room.id} value={room.roomNumber}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  🏥 Room {room.roomNumber} - {room.roomType?.toUpperCase()}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                  ✅ {room.availableBeds?.filter(bed => !bed.isOccupied).length}/{room.capacity} beds available • {room.department || 'General'}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ));
                        })()
                      ) : (
                        <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                          ⚠️ No rooms loaded. Click "Refresh Rooms" button above.
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required>
                    <InputLabel>Bed Number *</InputLabel>
                    <Select 
                      value={registrationForm.bedNumber}
                      label="Bed Number *"
                      onChange={(e) => {
                        console.log('✅ Bed selected (OP):', e.target.value);
                        setRegistrationForm({...registrationForm, bedNumber: e.target.value as string});
                      }}
                      disabled={!registrationForm.roomNumber || loadingRooms}
                      onOpen={() => {
                        console.log('🛏️ Bed dropdown opened (OP)');
                        console.log('📍 Selected room:', registrationForm.roomNumber);
                        const room = availableRooms.find(r => r.roomNumber === registrationForm.roomNumber);
                        console.log('🔍 Found room:', room);
                        if (room) {
                          console.log('📊 Room beds:', room.availableBeds);
                          const available = room.availableBeds?.filter(bed => {
                            const isOcc = typeof bed === 'object' ? (bed.isOccupied === true) : false;
                            return !isOcc;
                          });
                          console.log('✅ Available beds:', available);
                        }
                      }}
                    >
                      {!registrationForm.roomNumber ? (
                        <MenuItem value="" disabled>Select room first</MenuItem>
                      ) : loadingRooms ? (
                        <MenuItem value="" disabled>Loading beds...</MenuItem>
                      ) : (() => {
                        const selectedRoom = availableRooms.find(room => room.roomNumber === registrationForm.roomNumber);
                        
                        console.log('🔎 Looking for room:', registrationForm.roomNumber);
                        console.log('📋 Available rooms:', availableRooms.map(r => r.roomNumber));
                        console.log('✅ Selected room found:', selectedRoom);
                        
                        if (!selectedRoom) {
                          return (
                            <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                              ⚠️ Room not found: {registrationForm.roomNumber}
                            </MenuItem>
                          );
                        }
                        
                        if (!selectedRoom.availableBeds || selectedRoom.availableBeds.length === 0) {
                          return (
                            <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                              ⚠️ No beds configured for this room
                            </MenuItem>
                          );
                        }
                        
                        // Filter beds - check isOccupied property carefully (OP Section)
                        const availableBeds = selectedRoom.availableBeds.filter(bed => {
                          const isOccupied = typeof bed === 'object' ? (bed.isOccupied === true) : false;
                          return !isOccupied;
                        });
                        
                        if (availableBeds.length === 0) {
                          return (
                            <MenuItem value="" disabled sx={{ color: '#EF4444' }}>
                              ⚠️ All {selectedRoom.availableBeds.length} beds occupied in this room
                            </MenuItem>
                          );
                        }
                        
                        return availableBeds.map((bed, index) => {
                          // Handle different bed data structures
                          const bedData = bed as any;
                          const bedNumber = bed.bedNumber || bedData.number || bedData.id || `${index + 1}`;
                          console.log(`🔍 Processing bed:`, bed, `Bed Number: ${bedNumber}`);
                          
                          return (
                            <MenuItem 
                              key={bedNumber} 
                              value={bedNumber}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#F3F4F6' }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography>🛏️ Bed {bedNumber}</Typography>
                                <Box sx={{ 
                                  backgroundColor: '#10B981', 
                                  color: 'white', 
                                  px: 1, 
                                  borderRadius: 1, 
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}>
                                  AVAILABLE
                                </Box>
                              </Box>
                            </MenuItem>
                          );
                        });
                      })()}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {/* Referred By - Optional for both */}
            <TextField
              fullWidth
              label="Referred By (Optional)"
              value={registrationForm.referredBy}
              onChange={(e) => setRegistrationForm({...registrationForm, referredBy: e.target.value})}
              placeholder="Referring doctor or hospital name"
              sx={{ mb: 4 }}
            />

            {/* Payment Details - Different for IP vs OP */}
            <Box sx={{ 
              mb: 4,
              p: 3,
              backgroundColor: '#FEF3F2',
              borderRadius: 2,
              border: '1px solid #FECACA'
            }}>
              <Typography variant="h6" sx={{ mb: 3, color: '#1F2937', display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <PaymentIcon sx={{ color: '#EF4444' }} />
                💰 {registrationForm.patientType === 'IP' ? 'Admission Payment Details' : 'Consultation Payment'}
              </Typography>
              
              {registrationForm.patientType === 'IP' ? (
                // IP Payment - More detailed
                <Box>
                  <Typography variant="body2" sx={{ color: '#374151', mb: 3, backgroundColor: '#F8FAFC', p: 2, borderRadius: 2 }}>
                    💳 <strong>Admission Payment:</strong> Set total estimated cost and initial deposit. Remaining amount can be collected later.
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3,
                    mb: 3
                  }}>
                    <TextField
                      fullWidth
                      required
                      label="Estimated Total Cost *"
                      type="number"
                      value={registrationForm.totalAmount}
                      onChange={(e) => setRegistrationForm({...registrationForm, totalAmount: e.target.value})}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, color: '#6B7280' }}>₹</Typography>
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Admission Deposit"
                      type="number"
                      value={registrationForm.initialPayment}
                      onChange={(e) => setRegistrationForm({...registrationForm, initialPayment: e.target.value})}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, color: '#6B7280' }}>₹</Typography>
                      }}
                    />
                  </Box>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3
                  }}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Method</InputLabel>
                      <Select 
                        value={registrationForm.paymentMethod}
                        label="Payment Method"
                        disabled={paymentCompleted || registrationCompleting}
                        onChange={(e) => setRegistrationForm({...registrationForm, paymentMethod: e.target.value})}
                      >
                        <MenuItem value="Cash">💵 Cash Payment</MenuItem>
                        <MenuItem value="Razorpay">💳 Online Payment (Razorpay)</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Payment Type</InputLabel>
                      <Select 
                        value={registrationForm.paymentType}
                        label="Payment Type"
                        onChange={(e) => setRegistrationForm({...registrationForm, paymentType: e.target.value})}
                      >
                        {PAYMENT_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  {/* Payment Summary Widget - Matching Mobile Dashboard */}
                  {registrationForm.totalAmount && (
                    <Box sx={{ 
                      backgroundColor: '#FFFFFF', 
                      p: 2, 
                      borderRadius: 2, 
                      mt: 2,
                      border: '1px solid #E5E7EB'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#374151' }}>
                        💰 Payment Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>Total Amount:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: '500', color: '#1F2937' }}>
                          ₹{parseFloat(registrationForm.totalAmount).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>Initial Payment:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: '500', color: '#1F2937' }}>
                          ₹{parseFloat(registrationForm.initialPayment || '0').toLocaleString()}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1F2937' }}>Due Amount:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#EF4444' }}>
                          ₹{(parseFloat(registrationForm.totalAmount) - parseFloat(registrationForm.initialPayment || '0')).toFixed(2)}
                        </Typography>
                      </Box>
                      {registrationForm.paymentMethod === 'Razorpay' && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6B7280' }}>Payment Status:</Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'bold', 
                            color: registrationCompleting ? '#7C3AED' : (paymentCompleted ? '#10B981' : '#F59E0B'),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            {registrationCompleting ? '🏥 Registering...' : (paymentCompleted ? '✅ Paid' : '⏳ Pending')}
                          </Typography>
                        </Box>
                      )}
                      {paymentCompleted && registrationForm.transactionId && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6B7280' }}>Transaction ID:</Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'bold', 
                            color: '#10B981',
                            fontSize: '0.75rem'
                          }}>
                            {registrationForm.transactionId.substring(0, 15)}...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                // OP Payment - Matching Mobile Dashboard
                <Box>
                  <Typography variant="body2" sx={{ color: '#374151', mb: 3, backgroundColor: '#F8FAFC', p: 2, borderRadius: 2 }}>
                    💰 <strong>Consultation Fee:</strong> Total service cost and initial payment collected during registration.
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3,
                    mb: 3
                  }}>
                    <TextField
                      fullWidth
                      required
                      label="Total Amount *"
                      type="number"
                      value={registrationForm.totalAmount}
                      onChange={(e) => setRegistrationForm({...registrationForm, totalAmount: e.target.value})}
                      placeholder="500.00"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, color: '#6B7280' }}>₹</Typography>
                      }}
                      helperText="Total treatment/service cost"
                    />
                    <TextField
                      fullWidth
                      label="Initial Payment"
                      type="number"
                      value={registrationForm.initialPayment}
                      onChange={(e) => setRegistrationForm({...registrationForm, initialPayment: e.target.value})}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1, color: '#6B7280' }}>₹</Typography>
                      }}
                      helperText="Amount paid during registration"
                    />
                  </Box>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3
                  }}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Method</InputLabel>
                      <Select 
                        value={registrationForm.paymentMethod}
                        label="Payment Method"
                        onChange={(e) => setRegistrationForm({...registrationForm, paymentMethod: e.target.value})}
                      >
                        <MenuItem value="Cash">💵 Cash Payment</MenuItem>
                        <MenuItem value="Razorpay">💳 Online Payment (Razorpay)</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Payment Type</InputLabel>
                      <Select 
                        value={registrationForm.paymentType}
                        label="Payment Type"
                        onChange={(e) => setRegistrationForm({...registrationForm, paymentType: e.target.value})}
                      >
                        {PAYMENT_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  {/* Payment Summary Widget - Matching Mobile Dashboard */}
                  {registrationForm.totalAmount && (
                    <Box sx={{ 
                      backgroundColor: '#FFFFFF', 
                      p: 2, 
                      borderRadius: 2, 
                      mt: 2,
                      border: '1px solid #E5E7EB'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#374151' }}>
                        💰 Payment Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>Total Amount:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: '500', color: '#1F2937' }}>
                          ₹{parseFloat(registrationForm.totalAmount).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>Initial Payment:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: '500', color: '#1F2937' }}>
                          ₹{parseFloat(registrationForm.initialPayment || '0').toLocaleString()}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1F2937' }}>Due Amount:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#EF4444' }}>
                          ₹{(parseFloat(registrationForm.totalAmount) - parseFloat(registrationForm.initialPayment || '0')).toFixed(2)}
                        </Typography>
                      </Box>
                      {registrationForm.paymentMethod === 'Razorpay' && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="body2" sx={{ color: '#6B7280' }}>Payment Status:</Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'bold', 
                            color: paymentCompleted ? '#10B981' : '#F59E0B',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            {paymentCompleted ? '✅ Paid' : '⏳ Pending'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          backgroundColor: '#F8FAFC', 
          borderTop: '1px solid #E5E7EB',
          gap: 2,
          position: 'sticky',
          bottom: 0,
          zIndex: 1
        }}>
          <Button 
            onClick={handleCloseRegistrationModal}
            variant="outlined"
            sx={{ 
              color: '#6B7280',
              borderColor: '#D1D5DB',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={() => {
              // CRITICAL: Prevent any action if payment is currently processing
              if (processingPayment) {
                console.log('⚠️ Payment in progress, ignoring button click');
                return;
              }
              
              // CRITICAL: Prevent any action if registration is completing
              if (registrationCompleting) {
                console.log('⚠️ Registration in progress, ignoring button click');
                return;
              }
              
              // Handle cash payment confirmation before registration
              if (registrationForm.paymentMethod === 'Cash' && parseFloat(registrationForm.initialPayment) > 0) {
                handleCashPayment();
              }
              
              // If payment is already completed, ONLY proceed with registration (never trigger payment again)
              if (paymentCompleted) {
                console.log('✅ Payment already completed, proceeding directly to registration (no payment)');
                // Directly call registration, bypassing any payment checks
                handlePatientRegistration();
                return;
              }
              
              // If Razorpay is selected but payment NOT completed, initiate payment
              if (registrationForm.paymentMethod === 'Razorpay' && !paymentCompleted) {
                console.log('💳 Initiating Razorpay payment for the first time...');
                handlePatientRazorpayPayment();
                return;
              }
              
              // For all other payment methods, proceed with registration
              handlePatientRegistration();
            }}
            disabled={
              !registrationForm.fullName || 
              !registrationForm.age || 
              !registrationForm.gender || 
              !registrationForm.phoneNumber || 
              !registrationForm.doctor || 
              !registrationForm.department ||
              !registrationForm.totalAmount ||
              processingPayment ||
              registrationCompleting ||
              (registrationForm.patientType === 'IP' && (
                !registrationForm.roomNumber || 
                !registrationForm.bedNumber ||
                !registrationForm.emergencyContact ||
                !registrationForm.allergies
              ))
            }
            sx={{ 
              backgroundColor: processingPayment ? '#F59E0B' : (registrationCompleting ? '#7C3AED' : (paymentCompleted ? '#10B981' : '#EF4444')),
              '&:hover': { 
                backgroundColor: processingPayment ? '#D97706' : (registrationCompleting ? '#6D28D9' : (paymentCompleted ? '#059669' : '#DC2626'))
              },
              '&:disabled': { backgroundColor: '#9CA3AF' },
              fontWeight: 'bold',
              px: 4,
              minWidth: '160px'
            }}
            startIcon={processingPayment || registrationCompleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : undefined}
          >
            {processingPayment 
              ? 'Processing Payment...' 
              : registrationCompleting
                ? 'Completing Registration...'
                : paymentCompleted 
                  ? '✅ Complete Registration'
                  : registrationForm.paymentMethod === 'Razorpay' 
                    ? '💳 Pay & Register'
                    : '📝 Register Patient'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Patient Modal */}
      <Dialog 
        open={showEditModal} 
        onClose={() => {
          setShowEditModal(false);
          setEditingPatient(null);
        }}
        maxWidth="md"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '95vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#F8FAFC', 
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EditIcon sx={{ color: '#3B82F6' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
              Edit Patient: {editingPatient?.name}
            </Typography>
          </Box>
          <IconButton onClick={() => {
            setShowEditModal(false);
            setEditingPatient(null);
          }}>
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Updating patient information for {editingPatient?.id}
          </Alert>

          {/* Basic Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                required
                label="Full Name"
                value={registrationForm.fullName}
                onChange={(e) => setRegistrationForm({...registrationForm, fullName: e.target.value})}
              />
              <TextField
                fullWidth
                required
                label="Age"
                type="number"
                value={registrationForm.age}
                onChange={(e) => setRegistrationForm({...registrationForm, age: e.target.value})}
              />
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select 
                  value={registrationForm.gender}
                  label="Gender"
                  onChange={(e) => setRegistrationForm({...registrationForm, gender: e.target.value})}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Blood Group"
                value={registrationForm.bloodGroup}
                onChange={(e) => setRegistrationForm({...registrationForm, bloodGroup: e.target.value})}
              />
            </Box>
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Contact Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                value={registrationForm.phoneNumber}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                  if (numericValue.length <= 10) {
                    setRegistrationForm({...registrationForm, phoneNumber: numericValue});
                  }
                }}
                placeholder="10 digit number"
                inputProps={{ 
                  maxLength: 10,
                  inputMode: 'numeric' as const
                }}
                helperText={
                  registrationForm.phoneNumber.length === 10
                    ? '\u2713 Valid phone number'
                    : `${10 - registrationForm.phoneNumber.length} digits remaining`
                }
                error={registrationForm.phoneNumber.length > 0 && registrationForm.phoneNumber.length !== 10}
                sx={{
                  '& .MuiFormHelperText-root': {
                    color: registrationForm.phoneNumber.length === 10 ? '#4CAF50' : '#FF9800'
                  }
                }}
              />
              <TextField
                fullWidth
                label="Emergency Contact"
                value={registrationForm.emergencyContact}
                onChange={(e) => setRegistrationForm({...registrationForm, emergencyContact: e.target.value})}
              />
              <TextField
                fullWidth
                label="Address"
                value={registrationForm.address}
                onChange={(e) => setRegistrationForm({...registrationForm, address: e.target.value})}
                sx={{ gridColumn: { sm: 'span 2' } }}
              />
            </Box>
          </Box>

          {/* Medical Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Medical Assignment
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Assigned Doctor</InputLabel>
                <Select 
                  value={registrationForm.doctor}
                  label="Assigned Doctor"
                  onChange={(e) => setRegistrationForm({...registrationForm, doctor: e.target.value})}
                >
                  {availableDoctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.name}>
                      {doctor.name} - {doctor.specialization}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                required
                label="Department"
                value={registrationForm.department}
                onChange={(e) => setRegistrationForm({...registrationForm, department: e.target.value})}
              />
            </Box>
          </Box>

          {/* Billing Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon sx={{ color: '#10B981' }} />
              Billing & Payment Details
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Important:</strong> Changing the Total Amount will update the Due Amount automatically. All changes are tracked and logged.
            </Alert>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Total Amount"
                type="number"
                value={registrationForm.editTotalAmount}
                onChange={(e) => {
                  const newTotal = parseFloat(e.target.value) || 0;
                  const paid = parseFloat(registrationForm.editPaidAmount) || 0;
                  const newDue = Math.max(0, newTotal - paid);
                  setRegistrationForm({
                    ...registrationForm, 
                    editTotalAmount: e.target.value,
                    editDueAmount: newDue.toString()
                  });
                }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                }}
              />
              <TextField
                fullWidth
                label="Paid Amount"
                type="number"
                value={registrationForm.editPaidAmount}
                disabled
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                }}
                helperText="Read-only (updated via payments)"
              />
              <TextField
                fullWidth
                label="Due Amount"
                type="number"
                value={registrationForm.editDueAmount}
                disabled
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    color: parseFloat(registrationForm.editDueAmount) > 0 ? '#EF4444' : '#10B981',
                    fontWeight: 'bold'
                  }
                }}
                helperText="Auto-calculated"
              />
            </Box>

            <TextField
              fullWidth
              label="Reason for Bill Change (Optional)"
              value={registrationForm.billChangeReason}
              onChange={(e) => setRegistrationForm({...registrationForm, billChangeReason: e.target.value})}
              multiline
              rows={2}
              placeholder="e.g., Additional treatment charges, Medicine costs, Room upgrade, etc."
              helperText="This will be saved in the change history"
            />

            {/* Display Bill Change History */}
            {editingPatient?.billChangeHistory && editingPatient.billChangeHistory.length > 0 && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#F3F4F6', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon fontSize="small" />
                  Bill Change History
                </Typography>
                <List dense>
                  {editingPatient.billChangeHistory.slice(-5).reverse().map((change, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            ₹{change.oldValue} → ₹{change.newValue} 
                            <Chip 
                              label={`+₹${change.newValue - change.oldValue}`} 
                              size="small" 
                              sx={{ ml: 1, fontSize: '0.7rem' }}
                              color={change.newValue > change.oldValue ? "error" : "success"}
                            />
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: '#6B7280' }}>
                            {new Date(change.changedAt).toLocaleString()} by {change.changedBy}
                            {change.reason && ` • ${change.reason}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#F8FAFC', borderTop: '1px solid #E5E7EB' }}>
          <Button 
            onClick={() => {
              setShowEditModal(false);
              setEditingPatient(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={async () => {
              if (!editingPatient) return;
              
              try {
                setIsLoading(true);
                
                // Get current user from auth
                const currentUser = auth.currentUser;
                
                // Check if Total Amount has changed
                const oldTotalAmount = editingPatient.totalAmount || 0;
                const newTotalAmount = parseFloat(registrationForm.editTotalAmount) || 0;
                const paidAmount = parseFloat(registrationForm.editPaidAmount) || 0;
                const dueAmount = parseFloat(registrationForm.editDueAmount) || 0;
                
                let updateData: any = {
                  name: registrationForm.fullName,
                  fullName: registrationForm.fullName,
                  age: parseInt(registrationForm.age),
                  gender: registrationForm.gender,
                  bloodGroup: registrationForm.bloodGroup,
                  phone: registrationForm.phoneNumber,
                  phoneNumber: registrationForm.phoneNumber,
                  emergencyContact: registrationForm.emergencyContact,
                  address: registrationForm.address,
                  doctor: registrationForm.doctor,
                  department: registrationForm.department,
                  allergies: registrationForm.allergies,
                  totalAmount: newTotalAmount,
                  paidAmount: paidAmount,
                  dueAmount: dueAmount,
                  updatedAt: Timestamp.now()
                };
                
                // If Total Amount changed, add to change history
                if (oldTotalAmount !== newTotalAmount) {
                  const changeRecord = {
                    changedBy: currentUser?.email || 'admin',
                    changedByUid: currentUser?.uid || '',
                    changedAt: new Date().toISOString(),
                    field: 'totalAmount',
                    oldValue: oldTotalAmount,
                    newValue: newTotalAmount,
                    reason: registrationForm.billChangeReason || 'No reason provided'
                  };
                  
                  // Get existing history or create new array
                  const existingHistory = editingPatient.billChangeHistory || [];
                  updateData.billChangeHistory = [...existingHistory, changeRecord];
                  
                  console.log('💰 Bill amount changed:', {
                    from: oldTotalAmount,
                    to: newTotalAmount,
                    by: currentUser?.email,
                    reason: changeRecord.reason
                  });

                  // Update related payment records in payments collection
                  console.log('🔄 Searching for payment records with patientId:', editingPatient.id);
                  console.log('🔍 Patient name for matching:', registrationForm.fullName);
                  
                  const paymentsRef = collection(db, 'payments');
                  
                  // Try multiple query strategies to find payments
                  let paymentsSnapshot = await getDocs(query(paymentsRef, where('patientId', '==', editingPatient.id)));
                  
                  // If not found by ID, try by patient name
                  if (paymentsSnapshot.docs.length === 0) {
                    console.log('⚠️ No payments found by patientId, trying by patientName...');
                    paymentsSnapshot = await getDocs(query(paymentsRef, where('patientName', '==', registrationForm.fullName)));
                  }
                  
                  console.log(`📊 Found ${paymentsSnapshot.docs.length} existing payment record(s)`);
                  
                  if (paymentsSnapshot.docs.length > 0) {
                    // Calculate total paid amount from ALL payment transactions
                    const totalPaidFromTransactions = paymentsSnapshot.docs.reduce((sum, payDoc) => {
                      const payData = payDoc.data();
                      const transactionAmount = payData.amount || payData.actualAmountPaid || 0;
                      return sum + transactionAmount;
                    }, 0);
                    
                    const newPatientDueAmount = Math.max(0, newTotalAmount - totalPaidFromTransactions);
                    
                    console.log(`💰 Total paid from all transactions: ₹${totalPaidFromTransactions}`);
                    console.log(`💰 New patient due amount: ₹${newPatientDueAmount}`);
                    
                    // Update ALL existing payment records for this patient
                    console.log('✏️ Updating ALL payment records with same dueAmount...');
                    const paymentUpdatePromises = paymentsSnapshot.docs.map(async (paymentDoc) => {
                      const paymentData = paymentDoc.data();
                      
                      console.log(`📝 Updating payment ${paymentDoc.id}:`, {
                        paymentAmount: paymentData.amount,
                        oldDue: paymentData.dueAmount,
                        newDue: newPatientDueAmount
                      });
                      
                      // ✅ ALL payment cards show the SAME due amount (patient's total due)
                      const newStatus = newPatientDueAmount === 0 ? 'paid' : 
                                       (totalPaidFromTransactions > 0 ? 'partial' : 'pending');
                      const newPaymentStatus = newPatientDueAmount === 0 ? 'Fully Paid' : 
                                              (totalPaidFromTransactions > 0 ? 'Partially Paid' : 'Pending');
                      const newStatusColor = newPatientDueAmount === 0 ? '#22C55E' : 
                                            (totalPaidFromTransactions > 0 ? '#F59E0B' : '#EF4444');
                      
                      await updateDoc(doc(db, 'payments', paymentDoc.id), {
                        totalAmount: newTotalAmount,
                        fullAmount: newTotalAmount,
                        dueAmount: newPatientDueAmount,        // ✅ SAME for all cards
                        remainingAmount: newPatientDueAmount,   // ✅ SAME for all cards
                        paidAmount: totalPaidFromTransactions,  // ✅ SAME for all cards
                        status: newStatus,
                        paymentStatus: newPaymentStatus,
                        statusColor: newStatusColor,
                        updatedAt: Timestamp.now()
                      });
                      
                      console.log(`✅ Payment ${paymentDoc.id} updated - dueAmount: ${newPatientDueAmount}`);
                    });

                    await Promise.all(paymentUpdatePromises);
                    console.log(`✅ Updated ${paymentsSnapshot.docs.length} payment record(s) - All statuses: ${newPatientDueAmount === 0 ? 'PAID' : 'PARTIAL/PENDING'}`);
                  } else {
                    // No existing payment record - create a new one if amount > 0
                    if (newTotalAmount > 0) {
                      console.log('📝 No existing payments found. Creating new payment record...');
                      const newPaymentRecord = {
                        patientId: editingPatient.id,
                        originalId: editingPatient.id,
                        patientName: registrationForm.fullName,
                        patientPhone: registrationForm.phoneNumber,
                        amount: newTotalAmount,
                        totalAmount: newTotalAmount,
                        fullAmount: newTotalAmount,
                        paidAmount: paidAmount,
                        actualAmountPaid: paidAmount,
                        dueAmount: dueAmount,
                        remainingAmount: dueAmount,
                        type: 'consultation',
                        method: 'Cash',
                        paymentMethod: 'Cash',
                        description: `Bill updated - ${registrationForm.billChangeReason || 'Amount adjusted'}`,
                        serviceName: 'Consultation/Treatment',
                        status: paidAmount >= newTotalAmount ? 'success' : 'pending',
                        paymentStatus: paidAmount >= newTotalAmount ? 'Fully Paid' : (paidAmount > 0 ? 'Partially Paid' : 'Pending'),
                        statusColor: paidAmount >= newTotalAmount ? '#22C55E' : (paidAmount > 0 ? '#F59E0B' : '#EF4444'),
                        patientType: editingPatient.patientType || 'OP',
                        serviceType: editingPatient.patientType || 'OP',
                        lastPaymentDate: new Date().toISOString().split('T')[0],
                        paymentDate: Timestamp.now(),
                        registrationDate: editingPatient.registrationDate || new Date().toISOString().split('T')[0],
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now()
                      };

                      const newPaymentDoc = await addDoc(paymentsRef, newPaymentRecord);
                      console.log('✅ Created new payment record with ID:', newPaymentDoc.id);
                    }
                  }

                  // Update related invoices
                  console.log('📄 Searching for invoices with patientId:', editingPatient.id);
                  const invoicesRef = collection(db, 'invoices');
                  
                  // Try multiple query strategies to find invoices
                  let invoicesSnapshot = await getDocs(query(invoicesRef, where('patientId', '==', editingPatient.id)));
                  
                  // If not found by ID, try by patient name
                  if (invoicesSnapshot.docs.length === 0) {
                    console.log('⚠️ No invoices found by patientId, trying by patientName...');
                    invoicesSnapshot = await getDocs(query(invoicesRef, where('patientName', '==', registrationForm.fullName)));
                  }
                  
                  console.log(`📊 Found ${invoicesSnapshot.docs.length} existing invoice(s)`);
                  
                  if (invoicesSnapshot.docs.length > 0) {
                    // Update ALL existing invoices for this patient
                    console.log('✏️ Updating existing invoices...');
                    const invoiceUpdatePromises = invoicesSnapshot.docs.map(async (invoiceDoc) => {
                      const invoiceData = invoiceDoc.data();
                      const invoicePaidAmount = invoiceData.paidAmount || 0;
                      const newInvoiceDueAmount = Math.max(0, newTotalAmount - invoicePaidAmount);
                      
                      console.log(`📝 Updating invoice ${invoiceDoc.id}:`, {
                        oldTotal: invoiceData.totalAmount,
                        newTotal: newTotalAmount,
                        paid: invoicePaidAmount,
                        newDue: newInvoiceDueAmount
                      });
                      
                      // Update items to reflect new total
                      const updatedItems = invoiceData.items && invoiceData.items.length > 0 
                        ? invoiceData.items.map((item: any, index: number) => 
                            index === 0 
                              ? { ...item, rate: newTotalAmount, amount: newTotalAmount }
                              : item
                          )
                        : [{
                            name: 'Consultation/Treatment Fee',
                            description: registrationForm.billChangeReason || 'Medical services',
                            quantity: 1,
                            rate: newTotalAmount,
                            amount: newTotalAmount
                          }];
                      
                      await updateDoc(doc(db, 'invoices', invoiceDoc.id), {
                        totalAmount: newTotalAmount,
                        amount: newTotalAmount,
                        dueAmount: newInvoiceDueAmount,
                        balance: newInvoiceDueAmount,
                        paymentStatus: invoicePaidAmount >= newTotalAmount ? 'Fully Paid' : (invoicePaidAmount > 0 ? 'Partially Paid' : 'Pending'),
                        status: invoicePaidAmount >= newTotalAmount ? 'paid' : 'pending',
                        items: updatedItems,
                        updatedAt: Timestamp.now()
                      });
                      
                      console.log(`✅ Successfully updated invoice ${invoiceDoc.id}`);
                    });

                    await Promise.all(invoiceUpdatePromises);
                    console.log(`✅ Updated ${invoicesSnapshot.docs.length} invoice(s)`);
                  } else {
                    // No existing invoice - create a new one if amount > 0
                    if (newTotalAmount > 0) {
                      console.log('📝 No existing invoices found. Creating new invoice...');
                      const invoiceNumber = `INV-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
                      const newInvoice = {
                        invoiceNumber: invoiceNumber,
                        patientId: editingPatient.id,
                        patientName: registrationForm.fullName,
                        patientPhone: registrationForm.phoneNumber,
                        patientType: editingPatient.patientType || 'OP',
                        serviceType: editingPatient.patientType || 'OP',
                        department: registrationForm.department,
                        doctor: registrationForm.doctor,
                        totalAmount: newTotalAmount,
                        amount: newTotalAmount,
                        paidAmount: paidAmount,
                        dueAmount: dueAmount,
                        balance: dueAmount,
                        paymentStatus: paidAmount >= newTotalAmount ? 'Fully Paid' : (paidAmount > 0 ? 'Partially Paid' : 'Pending'),
                        status: paidAmount >= newTotalAmount ? 'paid' : 'pending',
                        date: new Date().toISOString().split('T')[0],
                        invoiceDate: new Date().toISOString().split('T')[0],
                        items: [{
                          name: 'Consultation/Treatment Fee',
                          description: registrationForm.billChangeReason || 'Medical services',
                          quantity: 1,
                          rate: newTotalAmount,
                          amount: newTotalAmount
                        }],
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now()
                      };

                      const newInvoiceDoc = await addDoc(invoicesRef, newInvoice);
                      console.log('✅ Created new invoice with number:', invoiceNumber, 'ID:', newInvoiceDoc.id);
                    }
                  }
                  
                  console.log('✅ Payment and invoice sync completed successfully');
                }
                
                // Update patient in Firebase
                const patientRef = doc(db, 'patients', editingPatient.id);
                await updateDoc(patientRef, updateData);
                
                setSnackbar({ 
                  open: true, 
                  message: oldTotalAmount !== newTotalAmount 
                    ? `Patient ${registrationForm.fullName} updated! Total Amount changed from ₹${oldTotalAmount} to ₹${newTotalAmount}`
                    : `Patient ${registrationForm.fullName} updated successfully!`, 
                  severity: 'success' 
                });
                setShowEditModal(false);
                setEditingPatient(null);
                await handleRefresh();
              } catch (error: any) {
                console.error('Error updating patient:', error);
                setSnackbar({ 
                  open: true, 
                  message: `Failed to update patient: ${error.message}`, 
                  severity: 'error' 
                });
              } finally {
                setIsLoading(false);
              }
            }}
            sx={{ backgroundColor: '#3B82F6' }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add Payment Modal */}
      <Dialog 
        open={showPaymentModal} 
        onClose={() => {
          setShowPaymentModal(false);
          setPaymentForm({
            amount: '',
            description: '',
            paymentMethod: 'Cash',
            paymentType: 'Consultation Fee',
            status: 'paid',
            transactionId: '',
            items: [{ name: '', description: '', quantity: 1, rate: 0, amount: 0 }],
            setTotalAmount: ''
          });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E5E7EB' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>💳 Add Payment Transaction</Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedPatient?.name} • {selectedPatient?.id}
              </Typography>
            </Box>
            <IconButton onClick={() => setShowPaymentModal(false)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          {/* Payment Summary Card */}
          {selectedPatient && (
            <Card sx={{ mb: 3, backgroundColor: '#F0F9FF', border: '1px solid #BFDBFE' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  📊 Current Payment Status
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Total Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3B82F6' }}>
                      ₹{(patientPayments.length > 0 ? patientPayments[patientPayments.length - 1].totalAmount || 0 : 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Paid Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                      ₹{(patientPayments.length > 0 ? patientPayments[patientPayments.length - 1].paidAmount || 0 : 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Due Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#EF4444' }}>
                      ₹{(patientPayments.length > 0 ? patientPayments[patientPayments.length - 1].dueAmount || 0 : 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Set Total Amount Field (Optional - for first payment or adjustment) */}
          {patientPayments.length === 0 && (
            <Card sx={{ mb: 3, p: 2, backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <InfoIcon sx={{ color: '#F59E0B' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    💼 Set Expected Total Amount (Optional)
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    If patient owes more than the current payment, set the total bill amount here. Due will be calculated automatically.
                  </Typography>
                </Box>
                <TextField
                  label="Total Bill Amount"
                  type="number"
                  value={paymentForm.setTotalAmount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, setTotalAmount: e.target.value })}
                  placeholder="e.g., 500"
                  sx={{ width: 180 }}
                  InputProps={{
                    startAdornment: '₹'
                  }}
                />
              </Box>
            </Card>
          )}

          {/* Service Items */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            🛒 Service Items
          </Typography>
          {paymentForm.items.map((item, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, backgroundColor: '#FAFAFA' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr 1fr auto' }, gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="Service Name"
                  value={item.name}
                  onChange={(e) => {
                    const newItems = [...paymentForm.items];
                    newItems[index].name = e.target.value;
                    setPaymentForm({ ...paymentForm, items: newItems });
                  }}
                  placeholder="e.g., Consultation, X-Ray, Medicine"
                />
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={item.quantity}
                  onChange={(e) => {
                    const newItems = [...paymentForm.items];
                    newItems[index].quantity = parseInt(e.target.value) || 1;
                    newItems[index].amount = newItems[index].quantity * newItems[index].rate;
                    setPaymentForm({ ...paymentForm, items: newItems });
                  }}
                />
                <TextField
                  fullWidth
                  label="Rate (₹)"
                  type="number"
                  value={item.rate}
                  onChange={(e) => {
                    const newItems = [...paymentForm.items];
                    newItems[index].rate = parseFloat(e.target.value) || 0;
                    newItems[index].amount = newItems[index].quantity * newItems[index].rate;
                    setPaymentForm({ ...paymentForm, items: newItems });
                  }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                  }}
                />
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={item.amount}
                  disabled
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                  }}
                  sx={{ backgroundColor: '#F0F9FF' }}
                />
                <IconButton 
                  color="error"
                  onClick={() => {
                    if (paymentForm.items.length > 1) {
                      const newItems = paymentForm.items.filter((_, i) => i !== index);
                      setPaymentForm({ ...paymentForm, items: newItems });
                    }
                  }}
                  disabled={paymentForm.items.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={item.description}
                onChange={(e) => {
                  const newItems = [...paymentForm.items];
                  newItems[index].description = e.target.value;
                  setPaymentForm({ ...paymentForm, items: newItems });
                }}
                placeholder="Additional details about the service"
                sx={{ mt: 2 }}
                size="small"
              />
            </Card>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              setPaymentForm({
                ...paymentForm,
                items: [...paymentForm.items, { name: '', description: '', quantity: 1, rate: 0, amount: 0 }]
              });
            }}
            sx={{ mb: 3 }}
          >
            Add Another Service
          </Button>

          {/* Total Amount Summary */}
          <Card sx={{ mb: 3, backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Total Bill Amount:
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                  ₹{paymentForm.items.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            💰 Payment Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentForm.paymentType}
                label="Payment Type"
                onChange={(e) => setPaymentForm({ ...paymentForm, paymentType: e.target.value })}
              >
                {PAYMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentForm.paymentMethod}
                label="Payment Method"
                onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
              >
                {PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>{method}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={paymentForm.status}
                label="Payment Status"
                onChange={(e) => setPaymentForm({ ...paymentForm, status: e.target.value })}
              >
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Transaction ID (Optional)"
              value={paymentForm.transactionId}
              onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })}
              placeholder="TXN123456789"
            />
          </Box>

          <TextField
            fullWidth
            label="Additional Notes"
            value={paymentForm.description}
            onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
            multiline
            rows={2}
            placeholder="Any additional information about this payment"
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#F8FAFC', borderTop: '1px solid #E5E7EB' }}>
          <Button 
            onClick={() => setShowPaymentModal(false)}
            disabled={savingPayment}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            startIcon={savingPayment ? <CircularProgress size={20} /> : (paymentForm.paymentMethod === 'Razorpay' ? <PaymentIcon /> : <AddIcon />)}
            disabled={savingPayment || paymentForm.items.every(item => !item.name || item.amount === 0)}
            onClick={async () => {
              if (!selectedPatient) return;

              // If Razorpay is selected, trigger Razorpay payment
              if (paymentForm.paymentMethod === 'Razorpay') {
                handleRazorpayPayment();
                return;
              }
              
              // Otherwise, save payment directly
              setSavingPayment(true);
              try {
                const totalAmount = paymentForm.items.reduce((sum, item) => sum + item.amount, 0);
                
                console.log('💰 Creating payment record for amount:', totalAmount);

                // ✅ NEW LOGIC: Simple payment record with ONLY this payment's amount
                // No cumulative calculations - each payment is independent
                const paymentRecord = {
                  paymentId: `PAY-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
                  patientId: selectedPatient.id,
                  patientName: selectedPatient.name,
                  
                  // THIS payment only (NOT cumulative)
                  amount: totalAmount,
                  
                  paymentDate: new Date().toISOString().split('T')[0],
                  paymentTime: new Date().toLocaleTimeString(),
                  
                  // Payment method details
                  paymentMethod: paymentForm.paymentMethod.toLowerCase(),
                  // ✅ Only add transactionId for Razorpay payments, not for Cash
                  ...(paymentForm.paymentMethod.toLowerCase() === 'razorpay' && paymentForm.transactionId ? { transactionId: paymentForm.transactionId } : {}),
                  
                  // Payment purpose/type
                  purpose: paymentForm.paymentType.toLowerCase().includes('consultation') ? 'consultation' : 
                           paymentForm.paymentType.toLowerCase().includes('surgery') ? 'surgery' :
                           paymentForm.paymentType.toLowerCase().includes('medicine') ? 'medicine' :
                           paymentForm.paymentType.toLowerCase().includes('test') ? 'tests' : 'admission',
                  
                  description: paymentForm.description || `Payment for ${paymentForm.paymentType}`,
                  
                  // Payment status
                  status: paymentForm.status === 'paid' ? 'completed' : 'pending',
                  
                  // Line items
                  items: paymentForm.items.filter(item => item.name && item.amount > 0).map(item => ({
                    name: item.name,
                    description: item.description || '',
                    quantity: item.quantity || 1,
                    rate: item.rate || item.amount,
                    amount: item.amount
                  })),
                  
                  // Metadata
                  createdAt: Timestamp.now(),
                  updatedAt: Timestamp.now()
                };

                console.log('💾 Saving payment to Firebase:', paymentRecord.paymentId);

                // Save payment to Firebase
                const paymentDoc = await addDoc(collection(db, 'payments'), paymentRecord);

                console.log('✅ Payment saved successfully:', paymentDoc.id);

                // ✅ Update patient record with new paid amount and due amount
                try {
                  console.log('💰 Updating patient billing record...');
                  const patientRef = doc(db, 'patients', selectedPatient!.id);
                  
                  // Calculate new paid amount (current paid + this payment)
                  const currentPaid = selectedPatient?.paidAmount || 0;
                  const newPaidAmount = currentPaid + totalAmount;
                  
                  // Calculate new due amount
                  const currentTotal = selectedPatient?.totalAmount || 0;
                  const newDueAmount = Math.max(0, currentTotal - newPaidAmount);
                  
                  await updateDoc(patientRef, {
                    paidAmount: newPaidAmount,
                    dueAmount: newDueAmount,
                    updatedAt: Timestamp.now()
                  });
                  
                  console.log('✅ Patient billing updated - Paid:', newPaidAmount, 'Due:', newDueAmount);
                  
                  // ✅ AUTO-UPDATE: If due amount is now 0, update ALL payment records to 'paid' status
                  if (newDueAmount === 0) {
                    console.log('🎉 Due amount is 0! Auto-updating ALL payment statuses to paid...');
                    try {
                      const allPaymentsQuery = query(
                        collection(db, 'payments'),
                        where('patientId', '==', selectedPatient!.id)
                      );
                      const allPaymentsSnapshot = await getDocs(allPaymentsQuery);
                      
                      console.log(`📝 Found ${allPaymentsSnapshot.docs.length} payment records to update`);
                      
                      const statusUpdatePromises = allPaymentsSnapshot.docs.map(async (payDoc) => {
                        const currentData = payDoc.data();
                        
                        // ✅ ALWAYS update ALL payments when patient due = 0
                        // Set dueAmount = 0 for ALL payment cards regardless of status
                        console.log(`   ✏️ Setting payment ${payDoc.id} dueAmount to 0`);
                        await updateDoc(doc(db, 'payments', payDoc.id), {
                          status: 'paid',
                          paymentStatus: 'Fully Paid',
                          statusColor: '#22C55E',
                          dueAmount: 0,
                          remainingAmount: 0,
                          totalAmount: currentTotal,
                          updatedAt: Timestamp.now()
                        });
                      });
                      
                      await Promise.all(statusUpdatePromises);
                      console.log('✅ All payment statuses auto-updated to PAID!');
                      
                      // Force immediate UI refresh by updating selectedPatient
                      if (selectedPatient) {
                        setSelectedPatient({
                          ...selectedPatient,
                          paidAmount: newPaidAmount,
                          dueAmount: 0
                        });
                      }
                    } catch (statusError) {
                      console.error('⚠️ Error auto-updating payment statuses:', statusError);
                    }
                  }
                } catch (updateError) {
                  console.error('⚠️ Payment saved but patient update failed:', updateError);
                  // Don't fail the payment if patient update fails
                }

                // ✅ Generate invoice for this payment (if paid)
                if (paymentForm.status === 'paid') {
                  console.log('📄 Generating invoice for payment...');
                  try {
                    await generateInvoiceForPayment(paymentDoc.id, paymentRecord, db);
                    console.log('✅ Invoice generated successfully');
                  } catch (invoiceError) {
                    console.error('⚠️ Payment saved but invoice generation failed:', invoiceError);
                    // Don't fail the payment if invoice generation fails
                  }
                }

                setSnackbar({
                  open: true,
                  message: `Payment of ₹${totalAmount.toLocaleString()} added successfully!${paymentForm.status === 'paid' ? ' Invoice generated.' : ''}`,
                  severity: 'success'
                });

                setShowPaymentModal(false);
                setPaymentForm({
                  amount: '',
                  description: '',
                  paymentMethod: 'Cash',
                  paymentType: 'Consultation Fee',
                  status: 'paid',
                  transactionId: '',
                  items: [{ name: '', description: '', quantity: 1, rate: 0, amount: 0 }],
                  setTotalAmount: ''
                });
              } catch (error: any) {
                console.error('Error adding payment:', error);
                setSnackbar({
                  open: true,
                  message: `Failed to add payment: ${error.message}`,
                  severity: 'error'
                });
              } finally {
                setSavingPayment(false);
              }
            }}
            sx={{ 
              backgroundColor: paymentForm.paymentMethod === 'Razorpay' ? '#2563EB' : '#10B981',
              '&:hover': {
                backgroundColor: paymentForm.paymentMethod === 'Razorpay' ? '#1E40AF' : '#059669'
              }
            }}
          >
            {savingPayment ? 'Processing...' : (paymentForm.paymentMethod === 'Razorpay' ? 'Pay with Razorpay' : 'Add Payment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Room Modal */}
      <Dialog 
        open={showChangeRoomModal} 
        onClose={() => {
          setShowChangeRoomModal(false);
          setPatientForRoomChange(null);
          setNewRoomNumber('');
          setNewBedNumber('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#F59E0B',
          color: 'white',
          fontWeight: 'bold'
        }}>
          Change Patient Room
        </DialogTitle>
        
        <DialogContent sx={{ mt: 3 }}>
          {patientForRoomChange && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Changing room for <strong>{patientForRoomChange.name}</strong>
                {patientForRoomChange.room && (
                  <> from Room <strong>{patientForRoomChange.room}</strong>, Bed <strong>{patientForRoomChange.bedNo}</strong></>
                )}
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>New Room</InputLabel>
                  <Select
                    value={newRoomNumber}
                    label="New Room"
                    onChange={(e) => {
                      setNewRoomNumber(e.target.value);
                      setNewBedNumber(''); // Reset bed selection when room changes
                    }}
                  >
                    {availableRooms.map((room) => (
                      <MenuItem 
                        key={room.id} 
                        value={room.roomNumber}
                        disabled={room.currentOccupancy >= room.capacity}
                      >
                        Room {room.roomNumber} - {room.roomType} 
                        ({room.currentOccupancy}/{room.capacity} occupied)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {newRoomNumber && (
                  <FormControl fullWidth>
                    <InputLabel>New Bed</InputLabel>
                    <Select
                      value={newBedNumber}
                      label="New Bed"
                      onChange={(e) => setNewBedNumber(e.target.value)}
                    >
                      {availableRooms
                        .find(r => r.roomNumber === newRoomNumber)
                        ?.availableBeds
                        ?.filter(bed => !bed.isOccupied)
                        ?.map((bed) => (
                          <MenuItem key={bed.bedNumber} value={bed.bedNumber}>
                            Bed {bed.bedNumber}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}

                {newRoomNumber && !availableRooms.find(r => r.roomNumber === newRoomNumber)?.availableBeds?.some(bed => !bed.isOccupied) && (
                  <Alert severity="warning">
                    No available beds in the selected room. Please choose a different room.
                  </Alert>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#F8FAFC', borderTop: '1px solid #E5E7EB' }}>
          <Button 
            onClick={() => {
              setShowChangeRoomModal(false);
              setPatientForRoomChange(null);
              setNewRoomNumber('');
              setNewBedNumber('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<ChangeRoomIcon />}
            disabled={!newRoomNumber || !newBedNumber}
            onClick={handleConfirmRoomChange}
            sx={{ 
              backgroundColor: '#F59E0B',
              '&:hover': { backgroundColor: '#D97706' }
            }}
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientsEnhanced;